import { createContext, h, toChildArray, cloneElement, Fragment, render } from 'preact';
import { useReducer, useMemo, useContext, useState, useRef, useCallback, useEffect, useLayoutEffect, useImperativeHandle } from 'preact/hooks';
import { produce, freeze } from 'immer';
import { forwardRef } from 'preact/compat';
import * as math from 'mathjs';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  for (var key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (excluded.indexOf(key) >= 0) continue;
      target[key] = source[key];
    }
  }
  return target;
}

const reducer$3 = produce((state, action) => {
  switch (action.type) {
    case 'TOTAL_FRAMES':
      {
        state.totalFrames = action.frames;
        return;
      }
    case 'BUFFERED_FRAMES':
      {
        state.bufferedFrames = action.frames;
        return;
      }
    case 'CURRENT_FRAME':
      {
        state.currentFrame = Math.min(state.totalFrames, Math.max(0, action.frame));
        return;
      }
    case 'PAUSE':
      {
        state.wasPlaying = state.isPlaying;
        state.isPlaying = false;
        return;
      }
    case 'TOGGLE':
      {
        state.wasPlaying = state.isPlaying;
        state.isPlaying = !state.isPlaying;
        return;
      }
    case 'RESTART':
      {
        state.wasPlaying = state.isPlaying;
        state.isPlaying = false;
        state.currentFrame = 0;
        return;
      }
    case 'NEXT':
      {
        if (!state.isPlaying) {
          var _action$frames;
          state.currentFrame = Math.min(state.totalFrames, state.currentFrame + ((_action$frames = action.frames) != null ? _action$frames : 1));
        }
        return;
      }
    case 'PREV':
      {
        if (!state.isPlaying) {
          var _action$frames2;
          state.currentFrame = Math.max(0, state.currentFrame - ((_action$frames2 = action.frames) != null ? _action$frames2 : 1));
        }
        return;
      }
    case 'START_SEEK':
      {
        // When seeking we save the play state so we can restore it after seeking.
        state.wasPlaying = state.isPlaying;
        state.isPlaying = false;
        state.currentFrame = action.frame;
        return;
      }
    case 'SEEK':
      {
        state.currentFrame = action.frame;
        return;
      }
    case 'STOP_SEEK':
      {
        state.wasPlaying = false;
        state.isPlaying = state.wasPlaying;
        return;
      }
    case 'RESET':
      {
        state.totalFrames = 0;
        state.currentFrame = 0;
        state.bufferedFrames = 0;
        state.isPlaying = false;
        state.wasPlaying = false;
        return;
      }
  }
});

const PlaybackContext = createContext(null);
// Handles playback state.
// It has been extracted because several different components in the app need access to this state.
const PlaybackProvider = ({
  value,
  children
}) => {
  const [{
    bufferedFrames,
    totalFrames,
    currentFrame,
    isPlaying
  }, dispatch] = useReducer(reducer$3, reducer$3({}, {
    type: 'RESET'
  }));
  const contextValue = useMemo(() => ({
    totalFrames,
    bufferedFrames,
    currentFrame,
    isPlaying,
    dispatch
  }), [totalFrames, bufferedFrames, currentFrame, isPlaying]);
  return h(PlaybackContext.Provider, {
    value: value != null ? value : contextValue
  }, children);
};
function usePlayback() {
  const context = useContext(PlaybackContext);
  if (!context) {
    throw new Error('usePlayback must be a child of PlaybackProvider');
  }
  return context;
}

// Filter out duplicates from an array.
function unique(list) {
  return Array.from(new Set(list));
}
// Updates valid options for each parameter.
function updateValidOptions(state) {
  // Parameters that have a value.
  const setParams = state.parameters.filter(param => !!param.value);
  state.parameters.forEach(param => {
    param.validOptions = unique(
    // Get the value of the parameter from each video that matches all of the set parameters.
    state.videos.filter(video => setParams.every(p => p.name === param.name || p.value === video.parameters[p.name])).map(video => video.parameters[param.name]))
    // Remove the parameter values that aren't in the optimized list.
    .filter(option => param.optimizedOptions.includes(option));
  });
}
// Updates the optimized options based on which is independent.
// This seeks to figure out which sets of constant parameter values
// result in the most options for the independent parameter.
function optimizeOptions(state) {
  const independentParam = state.parameters.find(param => param.name === state.independent);
  // If no independent parameter, then the unoptimized regular options.
  if (!independentParam) {
    state.parameters.forEach(param => {
      param.optimizedOptions = param.options;
    });
  } else {
    // Get constant parameters.
    const constantParams = state.parameters.filter(param => param !== independentParam);
    // Create dictionary to count groups of constant values.
    const countDict = {};
    // Count each video by the value of its constant parameters.
    state.videos.forEach(video => {
      // Hashes the list of constant parameter values and uses that as a key.
      // Videos with the same constant parameter values will then have the same key.
      const paramValues = constantParams.map(param => video.parameters[param.name]);
      const hash = paramValues.toString();
      let entry = countDict[hash];
      if (!entry) {
        entry = {
          params: paramValues,
          count: 0,
          independentValues: []
        };
        countDict[hash] = entry;
      }
      // For each video that has the same parameters:
      // Increment the count of options and save the value of the independent parameter.
      entry.count += 1;
      entry.independentValues.push(video.parameters[independentParam.name]);
    });
    // Now that everything is sorted, we don't need the keys anymore.
    const counts = Object.values(countDict);
    // Find the maximum number of videos per constant parameter combination.
    const max = Math.max(...counts.map(entry => entry.count));
    // Get a list of parameter combinations that have the maximum count.
    const optimizedList = counts.filter(entry => entry.count === max);
    // Convert list of parameter combinations to unique list by parameter.
    constantParams.forEach((param, i) => {
      param.optimizedOptions = unique(optimizedList.map(entry => entry.params[i]));
    }, {});
    independentParam.optimizedOptions = unique(optimizedList.reduce((arr, entry) => arr.concat(entry.independentValues), []));
  }
}
// Initialize selection state from a list of parameters, and videos.
function init(arg) {
  const {
    videos,
    parameters: parameterConfigs
  } = arg;
  const state = {
    parameters: parameterConfigs.map(param => ({
      name: param.name,
      title: param.title,
      value: param.initial,
      selectable: param.selectable,
      visible: param.visible,
      options: unique(videos.map(video => video.parameters[param.name])).sort((a, b) => a.localeCompare(b, undefined, {
        numeric: true
      })),
      optimizedOptions: [],
      validOptions: []
    })),
    currentVideo: videos.find(video => parameterConfigs.every(param => video.parameters[param.name] === param.initial)),
    videos,
    isExpanded: false,
    canExpand: parameterConfigs.some(param => param.selectable)
  };
  optimizeOptions(state);
  updateValidOptions(state);
  return state;
}
const reducer$2 = produce((state, action) => {
  switch (action.type) {
    case 'PARAMETER':
      {
        const param = state.parameters.find(param => param.name === action.name);
        if (param) {
          param.value = action.value;
          updateValidOptions(state);
        }
        return;
      }
    case 'INDEPENDENT':
      {
        state.parameters.forEach(param => delete param.value);
        state.independent = action.name;
        optimizeOptions(state);
        updateValidOptions(state);
        return;
      }
    case 'LOAD':
      {
        const video = state.videos.find(video => state.parameters.every(param => video.parameters[param.name] === param.value));
        if (video) {
          state.isExpanded = false;
          state.currentVideo = video;
        }
        return;
      }
    case 'CANCEL':
      {
        state.isExpanded = false;
        state.parameters.forEach(param => {
          param.value = state.currentVideo.parameters[param.name];
        });
        updateValidOptions(state);
        return;
      }
    case 'CLEAR':
      {
        state.parameters.forEach(param => {
          if (param.selectable) {
            param.value = undefined;
          }
        });
        state.independent = undefined;
        optimizeOptions(state);
        updateValidOptions(state);
        return;
      }
    case 'OPEN':
      {
        if (state.canExpand) {
          state.isExpanded = true;
        }
        return;
      }
  }
});

const SelectionContext = createContext(null);
const CurrentVideoContext = createContext(null);
const SelectionProvider = ({
  config,
  videos,
  children,
  value
}) => {
  var _config$enableIndepen, _config$parameters, _value$currentVideo;
  const enableIndependence = (_config$enableIndepen = config == null ? void 0 : config.enableIndependence) != null ? _config$enableIndepen : false;
  const [{
    currentVideo,
    parameters,
    canExpand,
    isExpanded,
    independent
  }, dispatch] = useReducer(reducer$2, {
    parameters: (_config$parameters = config == null ? void 0 : config.parameters) != null ? _config$parameters : [],
    videos
  }, init);
  const selectionContextValue = useMemo(() => ({
    parameters,
    enabled: parameters.filter(param => param.visible).length > 0,
    enableIndependence,
    isExpanded,
    canExpand,
    independent,
    dispatch
  }), [parameters, canExpand, isExpanded, independent, dispatch, enableIndependence]);
  const videoContextValue = useMemo(() => currentVideo, [currentVideo]);
  return h(SelectionContext.Provider, {
    value: value != null ? value : selectionContextValue
  }, h(CurrentVideoContext.Provider, {
    value: (_value$currentVideo = value == null ? void 0 : value.currentVideo) != null ? _value$currentVideo : videoContextValue
  }, children));
};
function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be a child of SelectionProvider');
  }
  return context;
}
function useCurrentVideo() {
  const context = useContext(CurrentVideoContext);
  if (context === null) {
    throw new Error('useCurrentVideo must be a child of SelectionProvider');
  }
  return context;
}

// Resize observer types aren't in typescript yet.
const ResizeObserver = window.ResizeObserver;
// Listens to changes in an elements size.
// Component rerenders when the size changes.
function useMeasuredElement() {
  const [[width, height], setDimensions] = useState([0, 0]);
  const observer = useRef(new ResizeObserver(entries => {
    requestAnimationFrame(() => {
      var _entries$;
      // We can't use the other properties on the event because in Safari 13 they are incorrect.
      // See https://bugs.webkit.org/show_bug.cgi?id=209947
      const el = (_entries$ = entries[0]) == null ? void 0 : _entries$.target;
      if (el) {
        setDimensions([el.clientWidth, el.clientHeight]);
      }
    });
  }));
  const ref = useCallback(el => {
    observer.current.disconnect();
    if (el) {
      observer.current.observe(el);
    }
  }, []);
  return {
    width,
    height,
    ref
  };
}

// Allows an element to be shared among several refs.
// Adapted from: https://github.com/facebook/react/issues/13029#issuecomment-522632038_
function useSharedRefs(...refs) {
  return useCallback(el => {
    refs.forEach(ref => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(el);
        } else {
          ref.current = el;
        }
      }
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [...refs]);
}

const MIN_WIDTH = 500;
// Cross browser implmentation for the FullScreen API.
const fullScreenEnabled = () => {
  var _document$fullscreenE;
  return (_document$fullscreenE = document.fullscreenEnabled) != null ? _document$fullscreenE : document.webkitFullscreenEnabled;
};
function getFullScreenElement() {
  var _document$fullscreenE2;
  return (_document$fullscreenE2 = document.fullscreenElement) != null ? _document$fullscreenE2 : document.webkitFullscreenElement;
}
const requestFullScreen = element => {
  const el = element;
  if (el.requestFullscreen) return el.requestFullscreen();else if (el.webkitRequestFullScreen) return el.webkitRequestFullScreen();else throw new Error('requestFullscreen not defined');
};
const exitFullScreen = () => {
  const el = document;
  if (el.exitFullscreen) return el.exitFullscreen();else if (el.webkitCancelFullScreen) return el.webkitCancelFullScreen();else throw new Error('exitFullscreen not defined');
};
const FullScreenContext = createContext(null);
// The FullScreenProvider is a wrapper around the element that is to be put into fullscreen mode.
// Children of the provider can listen to fullscreen state or put the element into fullscreen.
const FullScreenProvider = ({
  children,
  enabled: _enabled = true
}) => {
  const {
    ref: ref1,
    width,
    height
  } = useMeasuredElement();
  // The element to put in fullscreen.
  const el = useRef(null);
  const [isFullScreen, setFullscreen] = useState(false);
  const [isSmallScreen, setSmallScreen] = useState(false);
  const [isNarrowScreen, setNarrowScreen] = useState(false);
  useEffect(() => {
    setSmallScreen(width > 0 && width < MIN_WIDTH && !isFullScreen);
    setNarrowScreen(height > width);
  }, [width, height, isFullScreen]);
  // This callback ref adds and removes fullscreenchange events.
  // This allows the hook to make the fullscreen state reactive.
  const ref2 = useCallback(element => {
    el.current = element;
    if (element && _enabled) {
      const handler = () => {
        const fullScreenElement = getFullScreenElement();
        setFullscreen(fullScreenElement === element);
      };
      element.addEventListener('fullscreenchange', handler);
      element.addEventListener('webkitfullscreenchange', handler);
      return () => {
        element.removeEventListener('fullscreenchange', handler);
        element.removeEventListener('webkitfullscreenchange', handler);
      };
    }
    return;
  }, [_enabled]);
  const ref = useSharedRefs(ref1, ref2);
  const toggleFullScreen = useCallback(async () => {
    if (fullScreenEnabled() && _enabled && el.current) {
      try {
        if (isFullScreen) {
          await exitFullScreen();
        } else {
          await requestFullScreen(el.current);
        }
      } catch (error) {
        console.log(error);
        alert('There was an error entering fullscreen.');
      }
    } else {
      alert('Fullscreen is not supported in your browser.');
    }
  }, [isFullScreen, _enabled]);
  const value = useMemo(() => ({
    fullScreenEnabled: fullScreenEnabled() && _enabled,
    isFullScreen,
    isSmallScreen,
    isNarrowScreen,
    toggleFullScreen
  }), [isFullScreen, isSmallScreen, isNarrowScreen, toggleFullScreen, _enabled]);
  return h(FullScreenContext.Provider, {
    value: value
  }, h("div", {
    ref: ref,
    class: "p-bg-white"
  }, children));
};
function useFullScreen() {
  const context = useContext(FullScreenContext);
  if (!context) {
    throw new Error('useFullScreen must be a child of FullScreenProvider');
  }
  return context;
}

const Loading = ({
  className,
  size
}) => {
  const innerClass = `
    p-loading p-absolute p-w-full p-h-full p-rounded-full
    ${size === 'lg' ? 'p-border-8' : 'p-border-4'}
  `;
  return h("div", {
    className: `p-bg-shadow p-flex p-items-center p-justify-center ${className}`
  }, h("div", {
    className: `
          p-inline-block p-relative
          ${size === 'lg' ? 'p-w-20 p-h-20' : 'p-w-8 p-h-8'}
        `
  }, h("div", {
    className: innerClass,
    style: {
      'animation-delay': '-0.45s'
    }
  }), h("div", {
    className: innerClass,
    style: {
      'animation-delay': '-0.3s'
    }
  }), h("div", {
    className: innerClass,
    style: {
      'animation-delay': '-0.15s'
    }
  }), h("div", {
    className: innerClass
  })));
};

// When adding/removing a tool we have to:
// update the count for that type of tool
// add/remove the tool from the player
const reducer$1 = produce((state, action) => {
  switch (action.action) {
    case 'ADD':
      {
        const toolType = state.types[action.typeId];
        if (!toolType.limit || toolType.count < toolType.limit) {
          toolType.count += 1;
          state.tools.push({
            id: state.idCounter,
            typeId: action.typeId
          });
          state.idCounter += 1;
        }
        break;
      }
    case 'REMOVE':
      {
        const i = state.tools.findIndex(tool => tool.id === action.id);
        if (i >= 0) {
          const tool = state.tools[i];
          const toolType = state.types[tool.typeId];
          toolType.count -= 1;
          state.tools.splice(i, 1);
        }
        break;
      }
    case 'TOGGLE':
      {
        const i = state.tools.findIndex(tool => tool.typeId === action.typeId);
        const toolType = state.types[action.typeId];
        if (i >= 0) {
          toolType.count -= 1;
          state.tools.splice(i, 1);
        } else {
          toolType.count += 1;
          state.tools.push({
            id: state.idCounter,
            typeId: action.typeId
          });
          state.idCounter += 1;
        }
      }
  }
});

// What tools are available and rendered.
const ToolContext = createContext(null);
// The dimensions of the tool overlay.
const OverlayContext = createContext(null);
// Manages available tools and overlay dimensions.
const ToolProvider = ({
  value,
  tools,
  children
}) => {
  const [overlaySize, setSize] = useState({
    width: 0,
    height: 0
  });
  const [videoAspect, setVideoAspect] = useState(16 / 9);
  const [state, dispatch] = useReducer(reducer$1, {
    idCounter: 1,
    types: tools.map((tool, i) => ({
      config: tool,
      type: tool.type,
      id: i,
      limit: tool.limit,
      count: 0,
      label: tool.label,
      icon: tool.icon,
      iconSrc: tool.iconSrc
    })),
    tools: []
  });
  const toolContextValue = useMemo(() => Object.assign({}, state, {
    dispatch
  }), [state, dispatch]);
  const overlayContextValue = useMemo(() => Object.assign({}, overlaySize, {
    videoAspect,
    setSize,
    setVideoAspect
  }), [overlaySize, videoAspect]);
  return h(OverlayContext.Provider, {
    value: value || overlayContextValue
  }, h(ToolContext.Provider, {
    value: value || toolContextValue
  }, children));
};
function useOverlayDimensions() {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlayDimensions must be a child of ToolProvider');
  }
  return context;
}
// Get the state of each tool type.
// This changes when a tool is added or removed.
function useToolTypes() {
  const context = useContext(ToolContext);
  if (!context) {
    throw new Error('useTools must be a child of ToolProvider');
  }
  return useMemo(() => ({
    types: context.types,
    dispatch: context.dispatch
  }), [context.types, context.dispatch]);
}
// Get and update the list of tool instances rendered in the player.
function useTools() {
  const context = useContext(ToolContext);
  if (!context) {
    throw new Error('useTools must be a child of ToolProvider');
  }
  return useMemo(() => ({
    tools: context.tools.map(tool => Object.assign({}, tool, {
      config: context.types[tool.typeId].config
    })),
    dispatch: context.dispatch
  }), [context]);
}

const FRAME_OFFSET = 0.7;
const PLAY_INTERVAL = 50;
const BUFFER_INTERVAL = 50;
const Video = ({
  children,
  preview: _preview = false,
  onError
}) => {
  var _video$playbackFrameR;
  const {
    isFullScreen
  } = useFullScreen();
  const video = useCurrentVideo();
  const {
    setVideoAspect
  } = useOverlayDimensions();
  const {
    isPlaying,
    currentFrame,
    dispatch
  } = usePlayback();
  const [isLoading, setLoading] = useState(false);
  const [isFailed, setFailed] = useState(false);
  const [isWebmError, setWebmError] = useState(false);
  const [isFormatError, setFormatError] = useState(false);
  const lastBuffered = useRef(null);
  const [isBuffering, setBufferingState] = useState(false);
  const videoElement = useRef(null);
  const playbackFrameRate = (_video$playbackFrameR = video == null ? void 0 : video.playbackFrameRate) != null ? _video$playbackFrameR : 29.97;
  const src = video == null ? void 0 : video.src;
  const isWebm = src == null ? void 0 : src.toLowerCase().endsWith('.webm');
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  // Convert a time to a count of frames.
  const toFrameCount = useCallback(time => {
    return Math.floor(time * playbackFrameRate);
  }, [playbackFrameRate]);
  // Reset buffering state when a new video is loaded.
  useEffect(() => {
    setBufferingState(true);
    setLoading(!!src);
    setFailed(false);
    dispatch({
      type: 'RESET'
    });
    if (!_preview) {
      videoElement.current.load();
    }
  }, [src, dispatch, _preview]);
  // Play and pause the video according to the playing prop.
  useEffect(() => {
    if (isPlaying) {
      // Play is asynchronous and throws weird errors.
      // See https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      videoElement.current.play().catch(() => {});
    } else {
      videoElement.current.pause();
    }
  }, [isPlaying]);
  // Emit timechange events while the video is playing.
  useEffect(() => {
    if (isPlaying) {
      const id = setInterval(() => {
        dispatch({
          type: 'CURRENT_FRAME',
          frame: toFrameCount(videoElement.current.currentTime)
        });
      }, PLAY_INTERVAL);
      return () => clearInterval(id);
    }
    return;
  }, [isPlaying, dispatch, toFrameCount]);
  // Emit loadedframeschange event while the video is buffering.
  useEffect(() => {
    if (isBuffering) {
      const id = setInterval(() => {
        const {
          buffered,
          duration
        } = videoElement.current;
        if (buffered.length > 0) {
          // Update loaded frame count.
          const loadedTime = buffered.end(buffered.length - 1);
          const bufferedFrames = toFrameCount(loadedTime);
          if (lastBuffered.current !== bufferedFrames) {
            dispatch({
              type: 'BUFFERED_FRAMES',
              frames: toFrameCount(loadedTime)
            });
            lastBuffered.current = bufferedFrames;
          }
          // Stop buffering when the loaded time is the same as the duration.
          if (Math.abs(loadedTime - duration) < 0.01) {
            setBufferingState(false);
          }
        }
      }, BUFFER_INTERVAL);
      return () => clearInterval(id);
    }
    return;
  }, [isBuffering, toFrameCount, dispatch]);
  // Set current time from frame count while not playing.
  useEffect(() => {
    if (!isPlaying) {
      videoElement.current.currentTime = (currentFrame + FRAME_OFFSET) / playbackFrameRate;
    }
  }, [isPlaying, currentFrame, playbackFrameRate]);
  const handleVideoError = error => {
    setLoading(false);
    setFailed(true);
    if (isWebm && (isSafari || isIOS)) {
      setWebmError(true);
    }
    if ((error == null ? void 0 : error.code) === 4) {
      setFormatError(true);
    }
    onError == null || onError(error);
  };
  const overlayClass = 'p-text-white p-absolute p-inset-0 p-flex p-items-center p-justify-center mx-4';
  return h("div", {
    className: `p-relative p-bg-black ${isFullScreen ? 'p-flex-grow' : ''}`
  }, !isFullScreen && h("div", {
    className: "p-video-aspect"
  }), isLoading && h(Loading, {
    className: "p-absolute p-inset-0",
    size: "lg"
  }), isFailed ? h("div", {
    className: overlayClass
  }, isWebmError ? 'This video uses our IRIS color tool, which is not compatible with older versions of iOS and Safari. If you want to use IRIS, you will need to upgrade Safari or iOS, or switch to another browser.' : isFormatError ? 'Video format is not supported by your browser' : 'Video failed to play.') : !src && h("div", {
    className: overlayClass
  }, "Configuration has no video to play."), h("video", {
    className: "p-absolute p-w-full p-h-full p-inset-0 p-text-white",
    ref: videoElement,
    src: _preview ? '' : src,
    "data-testid": "video",
    crossOrigin: "use-credentials",
    onLoadedMetadata: e => {
      const el = e.currentTarget;
      setVideoAspect(el.videoWidth / el.videoHeight);
      dispatch({
        type: 'TOTAL_FRAMES',
        frames: toFrameCount(el.duration)
      });
    },
    onCanPlay: () => setLoading(false),
    onError: e => handleVideoError(e.target.error),
    onEnded: () => dispatch({
      type: 'PAUSE'
    }),
    playsInline: true,
    "aria-hidden": true
  }, "Your browser does not support video playback."), children);
};

const _excluded$9 = ["value", "children"];
const PointerContext = createContext(null);
// PointerTarget children of PointerBounds will have pointers calculated with respect to the PointerBounds.
const PointerBounds = _ref => {
  let {
      value,
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$9);
  const ref = useRef(null);
  // Get a pointer relative to its bounds.
  const getPoint = useCallback(pointer => {
    if (ref.current) {
      const {
        x,
        y,
        width,
        height
      } = ref.current.getBoundingClientRect();
      return {
        x: (pointer.clientX - x) / width,
        y: (pointer.clientY - y) / height
      };
    } else {
      return {
        x: pointer.clientX,
        y: pointer.clientY
      };
    }
  }, []);
  const contextValue = useMemo(() => ({
    getPoint
  }), [getPoint]);
  // PointerBounds is a wrapper component around a single element.
  const [child] = toChildArray(children);
  const childRef = useSharedRefs(...(typeof child === 'object' && child.ref ? [ref, child.ref] : [ref]));
  return h(PointerContext.Provider, {
    value: value || contextValue
  }, h(PointerTarget, props, child && typeof child === 'object' ? cloneElement(child, {
    ref: childRef
  }) : child));
};
function usePointerBounds() {
  const context = useContext(PointerContext);
  if (!context) {
    throw new Error('usePointerBounds must be a child of a PointerBounds component.');
  }
  return context;
}

// Abstracts away touch and mouse events to click, drag and gesture events.
// and makes them relative to the nearest PointerBounds component.
const PointerTarget = ({
  children,
  onClick,
  onTouch,
  onDragStart,
  onDrag,
  onDragEnd,
  onGestureStart,
  onGesture,
  onGestureEnd
}) => {
  const {
    getPoint
  } = usePointerBounds();
  const [isDragging, setDragging] = useState(false);
  // mousemove and mouseup don't fire on the element while dragging,
  // so we have to add them to the window while dragging
  // Map window mousemove event to onDrag while dragging.
  useEffect(() => {
    if (isDragging && onDrag) {
      const onMouseMove = e => {
        e.preventDefault();
        e.stopPropagation();
        onDrag(getPoint(e));
      };
      window.addEventListener('mousemove', onMouseMove);
      return () => {
        window.removeEventListener('mousemove', onMouseMove);
      };
    }
    return;
  }, [isDragging, onDrag, getPoint]);
  // Map window mouseup event to onDragEnd while dragging.
  useEffect(() => {
    if (isDragging) {
      const onMouseUp = e => {
        if (onDragEnd) {
          e.preventDefault();
          e.stopPropagation();
          onDragEnd && onDragEnd();
        }
        setDragging(false);
      };
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mouseup', onMouseUp);
      };
    }
    return;
  }, [isDragging, onDragEnd]);
  // PointerTarget attaches events to its first child, so children must be components or DOM elements.
  const child = toChildArray(children)[0];
  if (typeof child === 'string' || typeof child === 'number') {
    throw new Error('PointerTarget does not accept primitives as children.');
  }
  return cloneElement(child, {
    onClick: e => onClick && onClick(getPoint(e)),
    onMouseDown: e => {
      const point = getPoint(e);
      onTouch && onTouch(point);
      setDragging(true);
      if (onDragStart) {
        e.stopPropagation();
        onDragStart(point);
      }
    },
    // Touch events are mapped to dragging events until there is more than one touch.
    // Multiple touches are converted to gesture events.
    onTouchStart: e => {
      if (e.touches.length === 1) {
        const point = getPoint(e.touches[0]);
        onTouch && onTouch(point);
        if (onDragStart) {
          e.stopPropagation();
          setDragging(true);
          onDragStart(point);
        }
      } else {
        if (isDragging) {
          onDragEnd && onDragEnd();
          setDragging(false);
        }
        if (onGestureStart) {
          e.stopPropagation();
          onGestureStart(Array.from(e.touches).map(touch => Object.assign({
            id: touch.identifier
          }, getPoint(touch))));
        }
      }
    },
    onTouchMove: e => {
      if (e.touches.length === 1) {
        if (onDrag && isDragging) {
          e.preventDefault();
          e.stopPropagation();
          onDrag(getPoint(e.touches[0]));
        }
      } else if (onGesture) {
        e.preventDefault();
        e.stopPropagation();
        onGesture(Array.from(e.touches).map(touch => Object.assign({
          id: touch.identifier
        }, getPoint(touch))));
      }
    },
    onTouchEnd: e => {
      if (isDragging) {
        if (onDragEnd) {
          e.stopPropagation();
          setDragging(false);
          onDragEnd();
        }
      } else if (e.touches.length === 0) {
        if (onGestureEnd) {
          e.stopPropagation();
          onGestureEnd();
        }
      }
    }
  });
};

// Allows users to see the progress of the video and quickly jump to a frame.
const SeekBar = () => {
  const {
    totalFrames,
    currentFrame,
    bufferedFrames,
    dispatch
  } = usePlayback();
  // Convert a pointer event to a frame number.
  function seekFrame(e) {
    const frame = Math.floor(e.x * totalFrames);
    return Math.max(0, Math.min(totalFrames, frame));
  }
  function onKeyDown(e) {
    let isHandled = true;
    switch (e.code) {
      case 'ArrowDown':
        dispatch({
          type: 'PREV',
          frames: e.shiftKey ? 10 : 1
        });
        break;
      case 'ArrowUp':
        dispatch({
          type: 'NEXT',
          frames: e.shiftKey ? 10 : 1
        });
        break;
      case 'Home':
        dispatch({
          type: 'CURRENT_FRAME',
          frame: 0
        });
        break;
      case 'End':
        dispatch({
          type: 'CURRENT_FRAME',
          frame: totalFrames
        });
        break;
      case 'PageUp':
        dispatch({
          type: 'NEXT',
          frames: 10
        });
        break;
      case 'PageDown':
        dispatch({
          type: 'PREV',
          frames: 10
        });
        break;
      default:
        isHandled = false;
        break;
    }
    if (isHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }
  const bufferedPercent = `${(bufferedFrames / totalFrames * 100).toFixed(0)}%`;
  return h("div", {
    className: "p-flex-grow p-p-2"
  }, h(PointerBounds, {
    onClick: e => {
      dispatch({
        type: 'START_SEEK',
        frame: seekFrame(e)
      });
      setTimeout(() => {
        dispatch({
          type: 'STOP_SEEK'
        });
      }, 2);
    },
    "data-testid": "seek-track"
  }, h("div", {
    className: " p-bg-gray-500 p-w-full p-h-full p-relative p-cursor-pointer focus-within:p-shadow-focus ",
    tabIndex: -1,
    onKeyDown: onKeyDown
  }, h("div", {
    className: "p-absolute p-bg-gray-200 p-h-full",
    style: {
      width: bufferedPercent
    },
    "data-testid": "buffered-progress"
  }, h("div", {
    id: "buffered-progress",
    className: "p-sr-only",
    "aria-hidden": true
  }, bufferedPercent, " Buffered")), h("div", {
    className: "p-absolute p-bg-gray-400 p-h-full",
    style: {
      width: `${currentFrame / totalFrames * 100}%`
    },
    "data-testid": "play-progress"
  }), h("div", {
    "data-testid": "pointer-reference",
    className: "p-absolute p-mx-2 p-h-full p-inset-x-0"
  }, h(PointerTarget, {
    onDragStart: e => dispatch({
      type: 'START_SEEK',
      frame: seekFrame(e)
    }),
    onDrag: e => dispatch({
      type: 'SEEK',
      frame: seekFrame(e)
    }),
    onDragEnd: () => dispatch({
      type: 'STOP_SEEK'
    }),
    "data-testid": "seek-thumb"
  }, h("div", {
    className: "p-absolute p-h-12 p-w-12 p--mt-2 p--ml-6 p-flex p-items-center p-justify-center focus:p-outline-none",
    style: {
      left: `${currentFrame / totalFrames * 100}%`
    },
    role: "slider",
    tabIndex: 0,
    "aria-valuemax": totalFrames,
    "aria-valuemin": 0,
    "aria-valuenow": currentFrame,
    "aria-label": "Seek Frame",
    "aria-describedby": "buffered-progress",
    "aria-valuetext": `Frame ${currentFrame} of ${totalFrames}`,
    "aria-orientation": "horizontal"
  }, h("div", {
    className: "p-bg-black p-h-8 p-w-4"
  })))))));
};

const library = {
  backward: h("svg", {
    width: "16px",
    height: "17px",
    viewBox: "0 0 16 17"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-64.000000, -732.000000)"
  }, h("g", {
    transform: "translate(1.000000, 712.639303)"
  }, h("path", {
    d: "M79,20 L66.2,28 L79,36 L79,20 Z M63,33.6952 L64.6,33.6952 L64.6,22.3048 L63,22.3048 L63,33.6952 Z"
  }))))),
  close: h("svg", {
    x: "0px",
    y: "0px",
    viewBox: "0 0 1000 1000",
    "enable-background": "new 0 0 1000 1000"
  }, h("g", null, h("path", {
    d: "M153.7,153.7C57.9,249.5,10,365.3,10,499c0,135.7,47.9,251.5,143.7,347.3l0,0C249.5,942.1,363.3,990,499,990c135.7,0,251.5-47.9,347.3-143.7C942.1,750.5,990,634.7,990,499c0-135.7-47.9-249.5-143.7-345.3l0,0C750.5,57.9,634.7,10,499,10C365.3,10,249.5,57.9,153.7,153.7z M209.6,211.6l2-2C289.4,129.7,387.2,89.8,499,89.8c113.8,0,209.6,39.9,291.4,121.8c79.8,77.8,119.8,175.6,119.8,287.4c0,113.8-39.9,209.6-119.8,291.4C708.6,870.3,612.8,910.2,499,910.2c-111.8,0-209.6-39.9-287.4-119.8C129.8,708.6,89.8,612.8,89.8,499C89.8,387.2,129.8,289.4,209.6,211.6z"
  }), h("path", {
    d: "M293.4,331.3c0,12,4,22,12,29.9L443.1,497L305.4,632.7c-8,8-12,18-12,29.9c0,10,4,18,12,26c8,8,18,12,28,12c12,0,20-4,27.9-10L499,552.9l135.7,137.7c8,6,16,10,28,10c12,0,21.9-4,27.9-10c8-8,12-18,12-28c0-12-4-21.9-12-29.9L554.9,497l135.7-135.7c8-8,12-18,12-27.9c0-12-4-22-12-29.9c-6-8-16-12-25.9-12c-12,0-21.9,4-29.9,12L499,441.1L363.3,303.4c-8-8-18-12-29.9-12c-10,0-20,4-28,12C297.4,311.4,293.4,321.4,293.4,331.3z"
  }))),
  forward: h("svg", {
    width: "16px",
    height: "17px",
    viewBox: "0 0 16 17"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-132.000000, -732.000000)"
  }, h("g", {
    transform: "translate(1.000000, 712.639303)"
  }, h("path", {
    d: "M147,20 L134.2,28 L147,36 L147,20 Z M131,33.6952 L132.6,33.6952 L132.6,22.3048 L131,22.3048 L131,33.6952 Z",
    transform: "translate(139.000000, 28.000000) rotate(-180.000000) translate(-139.000000, -28.000000) "
  }))))),
  lock: h("svg", {
    width: "22px",
    height: "22px",
    viewBox: "0 0 22 22"
  }, h("g", {
    transform: "matrix(.04252 0 0 .04252 3 2.958)"
  }, h("path", {
    d: "m299.02 161.26h-185.84v-46.28c0-41.34 33.635-74.979 74.979-74.979 33.758 0 63.51 22.716 72.36 55.24 2.898 10.657 13.888 16.946 24.547 14.05 10.659-2.898 16.949-13.889 14.05-24.548-13.57-49.896-59.2-84.74-110.96-84.74-63.4 0-114.98 51.58-114.98 114.98v46.715c-9.06 1.902-15.888 9.952-15.888 19.571v175.05c0 11.03 8.972 20 20 20h221.73c11.03 0 20-8.972 20-20v-175.05c0-11.03-8.972-20-20-20"
  }), h("path", {
    d: "m215.02 310.91c.408 2.162-1.058 3.931-3.258 3.931h-46.677c-2.2 0-3.666-1.769-3.258-3.931l7.432-39.39c-5.626-5.131-9.157-12.52-9.157-20.734 0-15.495 12.561-28.06 28.06-28.06 15.495 0 28.06 12.561 28.06 28.06 0 7.991-3.346 15.195-8.707 20.305z"
  }))),
  pause: h("svg", {
    width: "17px",
    height: "22px",
    viewBox: "0 0 17 22"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-99.000000, -729.000000)"
  }, h("g", {
    transform: "translate(1.000000, 712.639303)"
  }, h("g", {
    transform: "translate(98.000000, 17.000000)"
  }, h("rect", {
    x: "10.5132212",
    y: "0",
    width: "6.30793269",
    height: "21.0264423"
  }), h("rect", {
    x: "0",
    y: "0",
    width: "6.30793269",
    height: "21.0264423"
  })))))),
  play: h("svg", {
    width: "19px",
    height: "22px",
    viewBox: "0 0 19 22"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-99.000000, -729.000000)"
  }, h("g", {
    transform: "translate(1.000000, 712.639303)"
  }, h("g", {
    transform: "translate(98.000000, 17.000000)"
  }, h("polygon", {
    transform: "translate(9.276372, 10.513221) rotate(-180.000000) translate(-9.276372, -10.513221) ",
    points: "0 10.5132212 18.5527432 3.55271368e-14 18.5527432 21.0264423"
  })))))),
  restart: h("svg", {
    width: "22px",
    height: "23px",
    viewBox: "0 0 22 23",
    "enable-background": "new 0 0 22 23"
  }, h("path", {
    d: "M11.827,0.639c-4.931,0-8.946,3.887-9.177,8.761H0l3.975,5.184L7.885,9.4H5.278c0.228-3.418,3.074-6.133,6.549-6.133 c3.624,0,6.571,2.947,6.571,6.571s-2.947,6.571-6.571,6.571c-0.092,0-0.177,0.011-0.263,0.021v2.591 c0.087,0.006,0.168,0.017,0.263,0.017c5.08,0,9.199-4.119,9.199-9.199S16.907,0.639,11.827,0.639z"
  })),
  'time-reset': h("svg", {
    width: "32",
    height: "32",
    viewBox: "0 0 32 32"
  }, h("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M28.0002 16C28.0002 22.6274 22.6276 28 16.0002 28V25C20.9707 25 25.0002 20.9706 25.0002 16C25.0002 11.0294 20.9707 7 16.0002 7C11.1132 7 7.13597 10.895 7.00356 15.75H10.2632L5.50002 21L0.736877 15.75H4.00271C4.13579 9.238 9.45627 4 16.0002 4C22.6276 4 28.0002 9.37258 28.0002 16ZM15 10H17V16H20V18H17H15V16V10ZM16 25H15V28H16V25Z"
  })),
  unlock: h("svg", {
    width: "22px",
    height: "22px",
    viewBox: "0 0 22 22"
  }, h("g", {
    transform: "matrix(.9939 0 0 .9939.06-1023.96)"
  }, h("path", {
    d: "m12.157 1046.55c.017.093-.045.168-.139.168h-1.997c-.094 0-.157-.076-.139-.168l.318-1.685c-.241-.219-.392-.536-.392-.887 0-.663.537-1.2 1.2-1.2.663 0 1.2.537 1.2 1.2 0 .342-.143.65-.372.869z"
  }), h("path", {
    d: "m14.216 1040.15h-6.415v-1.98c0-1.769 1.439-3.207 3.207-3.207 1.769 0 3.207 1.439 3.207 3.207zm1.711.018v-1.998c0-2.712-2.206-4.919-4.919-4.919-2.712 0-4.919 2.207-4.919 4.919v1.998c-.388.081-.68.426-.68.837v7.488c0 .472.384.855.856.855h9.485c.472 0 .856-.384.856-.855v-7.488c0-.411-.292-.756-.68-.837"
  }))),
  tools: h("svg", {
    width: "20px",
    height: "19px",
    viewBox: "0 0 20 19"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-970.000000, -36.000000)"
  }, h("g", {
    transform: "translate(53.216346, 21.000000)"
  }, h("g", {
    transform: "translate(902.000000, 0.000000)"
  }, h("path", {
    d: "M22.9496016,27.9847453 L18.8088375,32.0922375 C18.6290109,32.2736485 18.4737422,32.478825 18.3541219,32.7053907 L18.1156734,33.1648594 L16.4520797,34.0125 L15.7921875,33.3518157 L16.6041797,31.6525735 L17.0628563,31.4133329 C17.2894219,31.2945047 17.5072734,31.1527032 17.6886844,30.9720844 L21.8294485,26.8645922 L22.9496016,27.9847453 Z M25.7460235,20.7132563 L29.9660063,16.4996109 C30.4286438,16.0369734 31.033875,15.8048625 31.6406907,15.8048625 C32.2451297,15.8048625 32.8519454,16.0361813 33.3145829,16.4996109 C33.7803891,16.9654172 34.0125,17.5746094 34.0125,18.1830094 C34.0125,18.7882406 33.7819735,19.3934719 33.3209204,19.854525 L29.1017297,24.0673781 L28.2786469,23.2450875 L32.292661,19.2374109 C32.4265407,19.1043234 32.4249563,18.8872641 32.2918688,18.7541766 C32.1579891,18.6202969 31.9425141,18.6195047 31.8086344,18.7533844 L27.7938282,22.761061 L27.0895735,22.0560141 L31.1035875,18.0491297 C31.2374672,17.91525 31.236675,17.6989828 31.1035875,17.5643109 C30.9697079,17.4312234 30.7526485,17.4296391 30.619561,17.5643109 L26.6047547,21.5711953 L25.7460235,20.7132563 Z M32.0320313,32.8242188 C31.5939516,32.8242188 31.2398438,32.4693188 31.2398438,32.0320313 C31.2398438,31.595536 31.5939516,31.2398438 32.0320313,31.2398438 C32.470111,31.2398438 32.8242188,31.595536 32.8242188,32.0320313 C32.8242188,32.4693188 32.470111,32.8242188 32.0320313,32.8242188 L32.0320313,32.8242188 Z M33.433411,30.64095 L23.9707313,21.1782703 C23.5294828,20.7370219 23.3037094,20.1238688 23.3504485,19.5027938 C23.44155,18.3263953 23.0375344,17.1191016 22.1391938,16.2183844 C21.3232406,15.4032234 20.2569563,15 19.1898797,15 C18.7882406,15 18.3858094,15.0578297 17.9976375,15.1711125 L20.5080797,17.6831391 C20.7805922,18.941925 18.9561844,20.7869297 17.6823469,20.5088719 L15.1719047,17.9976375 C15.0578297,18.3866016 15,18.789825 15,19.1922563 C15,20.2593328 15.4040156,21.3240328 16.2175922,22.139986 C17.116725,23.0391188 18.3256031,23.4431344 19.5027938,23.3512406 C20.1230766,23.3037094 20.7362297,23.530275 21.176686,23.9715235 L30.6401579,33.4342032 C31.0109016,33.8049469 31.5139407,34.0125 32.0367844,34.0125 C33.1204969,34.0125 34.0125,33.1355485 34.0125,32.0367844 C34.0125,31.532161 33.8192063,31.0267454 33.433411,30.64095 L33.433411,30.64095 Z"
  })))))),
  stopwatch: h("svg", {
    width: "18px",
    height: "21px",
    viewBox: "0 0 18 21"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-973.000000, -288.000000)"
  }, h("g", {
    transform: "translate(53.216346, 21.000000)"
  }, h("g", {
    transform: "translate(908.000000, 259.000000)"
  }, h("g", {
    transform: "translate(11.783654, 8.000000)"
  }, h("mask", {
    id: "stopwatch-mask"
  }, h("polygon", {
    id: "path-1",
    points: "1.18584613e-20 0.000175 1.18584613e-20 21 17.4997375 21 17.4997375 0.000175 0 0.000175"
  })), h("g", {
    id: "Clip-2"
  }), h("path", {
    d: "M16.2624875,2.625175 L17.4997375,3.862425 L15.8861878,5.48695356 C15.4819378,5.06345356 15.0426878,4.67670356 14.5736878,4.32495356 L16.2624875,2.625175 Z M6.9997375,1.907675 L6.9997375,0.000175 L10.4997375,0.000175 L10.5445955,2.53877682 C9.2863455,2.32702682 8.2765955,2.33140182 7.0445955,2.53877682 M8.7497375,19.250175 C4.8901125,19.250175 1.7497375,16.1098 1.7497375,12.250175 C1.7497375,8.39055 4.8901125,5.250175 8.7497375,5.250175 C12.6093625,5.250175 15.7497375,8.39055 15.7497375,12.250175 C15.7497375,16.1098 12.6093625,19.250175 8.7497375,19.250175 M8.7497375,3.500175 C3.9179875,3.500175 -0.0002625,7.41755 -0.0002625,12.250175 C-0.0002625,17.081925 3.9179875,21.000175 8.7497375,21.000175 C13.5823625,21.000175 17.4997375,17.081925 17.4997375,12.250175 C17.4997375,7.41755 13.5823625,3.500175 8.7497375,3.500175 M9.6247375,10.500175 L9.0236125,7.000175 L8.4898625,7.000175 L7.8747375,10.500175 C7.3541125,10.802925 6.9997375,11.361175 6.9997375,12.006925 C6.9997375,12.9738 7.7837375,13.756925 8.7497375,13.756925 C9.7166125,13.756925 10.4997375,12.9738 10.4997375,12.006925 C10.4997375,11.361175 10.1462375,10.802925 9.6247375,10.500175",
    id: "Fill-1",
    fill: "#FFFFFF",
    mask: "url(#stopwatch-mask)"
  }))))))),
  calibration: h("svg", {
    width: "28",
    height: "28",
    viewBox: "0 0 28 28"
  }, h("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M0 21V16V12V7H4V12H24V7H28V12V16V21H24V16H4V21H0Z"
  })),
  fullscreen: h("svg", {
    width: "27.4px",
    height: "23px",
    viewBox: "0 0 27.4 23"
  }, h("polygon", {
    points: "7.6,7.1 7.6,0 5.4,0 5.4,4.9 0,4.9 0,7.1 \t\t\t"
  }), h("polygon", {
    points: "27.4,7.1 27.4,4.9 22,4.9 22,0 19.8,0 19.8,7.1 \t\t\t"
  }), h("polygon", {
    points: "22,23 22,18.2 27.4,18.2 27.4,16 19.8,16 19.8,23 \t\t\t"
  }), h("polygon", {
    points: "7.6,23 7.6,16 0,16 0,18.2 5.4,18.2 5.4,23 \t\t\t"
  })),
  check: h("svg", {
    width: "29.76px",
    height: "23.383px",
    viewBox: "6.12 160.38 29.76 23.383"
  }, h("path", {
    d: "M31.626,160.38l-14.877,14.877l-6.377-6.374l-4.251,4.251l6.377,6.377l1.063,1.063l3.189,3.189 l19.131-19.134L31.626,160.38z"
  })),
  dot: h("svg", {
    width: "3.6px",
    height: "3.6px",
    viewBox: "0 0 3.6 3.6"
  }, h("circle", {
    cx: "1.8",
    cy: "1.8",
    r: "1.8"
  })),
  grid: h("svg", {
    width: "20px",
    height: "20px",
    viewBox: "0 0 20 20"
  }, h("path", {
    fill: "#FFFFFF",
    d: "M0,0v20h20V0H0z M18.5,5H15V1.5h3.5V5z M10.3,5V1.5h3.9V5H10.3z M14.2,5.8v3.9h-3.9V5.8H14.2z M9.6,5H5.7 V1.5h3.9V5z M9.6,5.8v3.9H5.7V5.8H9.6z M5,9.6H1.5V5.7H5V9.6z M5,10.4v3.9H1.5v-3.9H5z M5.7,10.4h3.9v3.9H5.7V10.4z M9.6,15v3.5H5.7 V15H9.6z M10.3,15h3.9v3.5h-3.9V15z M10.3,14.2v-3.9h3.9v3.9H10.3z M15,10.4h3.5v3.9H15V10.4z M15,9.6V5.7h3.5v3.9H15z M5,1.5V5H1.5 V1.5H5z M1.5,15H5v3.5H1.5V15z M15,18.5V15h3.5v3.5H15z"
  })),
  'half-protractor': h("svg", {
    width: "20px",
    height: "10.741px",
    viewBox: "0 0 20 10.741"
  }, h("path", {
    d: "M20,10.041c0-3.6-1.9-6.9-5-8.7c-2.3-1.3-5-1.7-7.6-1s-4.7,2.3-6.1,4.7c-0.9,1.5-1.3,3.3-1.3,5v0.7h20 L20,10.041z M1.6,9.341c0.1-1.1,0.4-2.2,0.9-3.2l2,1.2l0.4-0.7l-2-1.2c0.7-1.1,1.6-1.9,2.6-2.6l1.2,2l0.6-0.4l-1.2-2 c0.5-0.3,1.1-0.5,1.7-0.7c0.6-0.2,1.2-0.2,1.8-0.3v4.4h0.8v-4.4c1.2,0.1,2.4,0.4,3.5,1l-1.2,2l0.6,0.4l1.2-2 c1.1,0.7,1.9,1.6,2.6,2.6l-2,1.2l0.4,0.7l2-1.2c0.5,1,0.8,2,0.9,3.2H1.6z"
  })),
  'not-fullscreen': h("svg", {
    width: "33px",
    height: "27px",
    viewBox: "0 0 33 27"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-971.000000, -726.000000)",
    "stroke-width": "0.25"
  }, h("g", {
    id: "video-controls",
    transform: "translate(1.000000, 712.639303)"
  }, h("g", {
    id: "fullscreen",
    transform: "translate(971.000000, 14.000000)"
  }, h("polygon", {
    points: "-1.13686838e-13 0.000149153088 -1.13686838e-13 7.17776864 2.20044164 7.17776864 2.20044164 2.23707259 7.61536176 2.23707259 7.61536176 0.000149153088"
  }), h("polygon", {
    points: "23.7868841 0.000149153088 23.7868841 2.23707259 29.2014375 2.23707259 29.2014375 7.17776864 31.4022459 7.17776864 31.4022459 0.000149153088"
  }), h("polygon", {
    points: "29.2016209 19.0229849 29.2016209 23.8626297 23.7870675 23.8626297 23.7870675 26.099926 31.4020625 26.099926 31.4020625 19.0229849"
  }), h("polygon", {
    points: "-1.13686838e-13 19.0229849 -1.13686838e-13 26.099926 7.61536176 26.099926 7.61536176 23.8626297 2.20044164 23.8626297 2.20044164 19.0229849"
  }), h("path", {
    d: "M7.61536176,19.0226866 L23.7871408,19.0226866 L23.7871408,7.07701573 L7.61536176,7.07701573 L7.61536176,19.0226866 Z M5.41492013,21.2599829 L25.9875825,21.2599829 L25.9875825,4.83971941 L5.41492013,4.83971941 L5.41492013,21.2599829 Z"
  })))))),
  protractor: h("svg", {
    width: "20.049px",
    height: "20.041px",
    viewBox: "0 0 20.049 20.041"
  }, h("rect", {
    x: "0.841",
    y: "9.641",
    width: "5.1",
    height: "0.8"
  }), h("rect", {
    x: "1.852",
    y: "13.526",
    transform: "matrix(-0.8663 0.4995 -0.4995 -0.8663 13.3063 24.2916)",
    width: "3.1",
    height: "0.8"
  }), h("rect", {
    x: "4.669",
    y: "16.347",
    transform: "matrix(-0.5001 0.866 -0.866 -0.5001 23.7893 19.6615)",
    width: "3.1",
    height: "0.7"
  }), h("rect", {
    x: "9.641",
    y: "14.141",
    width: "0.8",
    height: "5.1"
  }), h("rect", {
    x: "13.566",
    y: "15.178",
    transform: "matrix(-0.8661 0.4999 -0.4999 -0.8661 34.3311 24.2593)",
    width: "0.7",
    height: "3.1"
  }), h("rect", {
    x: "16.315",
    y: "12.323",
    transform: "matrix(-0.5 0.866 -0.866 -0.5 37.0867 6.3349)",
    width: "0.8",
    height: "3.1"
  }), h("rect", {
    x: "14.141",
    y: "9.641",
    width: "5.1",
    height: "0.8"
  }), h("rect", {
    x: "15.149",
    y: "5.836",
    transform: "matrix(-0.8663 0.4995 -0.4995 -0.8663 34.28 3.2981)",
    width: "3.1",
    height: "0.8"
  }), h("rect", {
    x: "12.379",
    y: "3.028",
    transform: "matrix(-0.5001 0.866 -0.866 -0.5001 23.8195 -6.9943)",
    width: "3.1",
    height: "0.7"
  }), h("rect", {
    x: "9.641",
    y: "0.841",
    width: "0.8",
    height: "5.1"
  }), h("rect", {
    x: "5.861",
    y: "1.822",
    transform: "matrix(-0.866 0.5001 -0.5001 -0.866 13.2767 3.1858)",
    width: "0.7",
    height: "3.1"
  }), h("path", {
    d: "M10.041,20.041c-1.7,0-3.4-0.4-5-1.3c-2.3-1.3-4-3.5-4.7-6.1s-0.3-5.3,1-7.6c1.3-2.3,3.5-4,6.1-4.7 s5.3-0.3,7.6,1c4.8,2.8,6.4,8.9,3.7,13.7l0,0C16.841,18.241,13.441,20.041,10.041,20.041z M10.041,1.541c-0.7,0-1.5,0.1-2.2,0.3 c-2.2,0.6-4,2-5.2,4c-1.1,2-1.4,4.3-0.8,6.4c0.6,2.2,2,4,4,5.2c4.1,2.3,9.3,0.9,11.6-3.1l0,0c2.3-4.1,0.9-9.3-3.1-11.6 C12.941,1.941,11.541,1.541,10.041,1.541z"
  }), h("rect", {
    x: "2.996",
    y: "4.614",
    transform: "matrix(-0.5 0.866 -0.866 -0.5 10.433 6.305)",
    width: "0.8",
    height: "3.1"
  })),
  'quarter-protractor': h("svg", {
    width: "10.8px",
    height: "10.8px",
    viewBox: "0 0 10.8 10.8"
  }, h("path", {
    d: "M10.7,0H10C4.5,0,0,4.5,0,10v0.8h10.8L10.7,0z M1.5,9.3c0.1-1.1,0.4-2.2,0.9-3.2l2,1.2l0.4-0.7l-2-1.2 c0.7-1,1.6-1.9,2.6-2.6l1.2,2l0.6-0.4L6,2.4c1-0.5,2-0.8,3.1-0.9v7.7L1.5,9.3z"
  })),
  'horizontal-ruler': h("svg", {
    width: "18px",
    height: "15px",
    viewBox: "0 0 18 15"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-972.000000, -143.000000)"
  }, h("g", {
    transform: "translate(53.216346, 21.000000)"
  }, h("g", null, h("g", {
    transform: "translate(907.862069, 109.342963)"
  }, h("g", {
    transform: "translate(11.000000, 12.000000)"
  }, h("path", {
    d: "M0.228070175,14.9547908 L17.9345479,14.9547908 L17.9345479,9.05263158 L0.228070175,9.05263158 L0.228070175,14.9547908 Z M14.9834683,12.0037112 L14.9834683,10.5281714 L16.4590081,10.5281714 L16.4590081,13.479251 L1.70360999,13.479251 L1.70360999,10.5281714 L3.1791498,10.5281714 L3.1791498,12.0037112 L3.9169197,12.0037112 L3.9169197,10.5281714 L5.39245951,10.5281714 L5.39245951,12.7414811 L6.13022942,12.7414811 L6.13022942,10.5281714 L7.60576923,10.5281714 L7.60576923,12.0037112 L8.34353914,12.0037112 L8.34353914,10.5281714 L9.81907895,10.5281714 L9.81907895,12.0037112 L10.5568489,12.0037112 L10.5568489,10.5281714 L12.0323887,10.5281714 L12.0323887,12.7414811 L12.7701586,12.7414811 L12.7701586,10.5281714 L14.2456984,10.5281714 L14.2456984,12.0037112 L14.9834683,12.0037112 Z",
    transform: "translate(9.081309, 12.003711) rotate(-360.000000) translate(-9.081309, -12.003711) "
  }), h("polygon", {
    transform: "translate(8.955800, 3.393851) rotate(-90.000000) translate(-8.955800, -3.393851) ",
    points: "8.95579972 -2.87719298 6.54385965 0.258329116 7.88382636 0.258329116 7.88382636 3.39358992 7.88382636 6.52937331 6.54385965 6.52937331 8.95579972 9.66489541 11.3677398 6.52937331 10.0277731 6.52937331 10.0277731 3.39358992 10.0277731 0.258329116 11.3677398 0.258329116"
  })))))))),
  'vertical-ruler': h("svg", {
    width: "16px",
    height: "19px",
    viewBox: "0 0 16 19"
  }, h("g", {
    "fill-rule": "evenodd"
  }, h("g", {
    transform: "translate(-973.000000, -91.000000)"
  }, h("g", {
    transform: "translate(53.216346, 21.000000)"
  }, h("g", null, h("g", {
    transform: "translate(907.000000, 59.737173)"
  }, h("g", {
    transform: "translate(13.000000, 10.000000)"
  }, h("path", {
    d: "M-5.28070175,12.7442645 L12.425776,12.7442645 L12.425776,6.84210526 L-5.28070175,6.84210526 L-5.28070175,12.7442645 Z M9.47469636,9.79318489 L9.47469636,8.31764507 L10.9502362,8.31764507 L10.9502362,11.2687247 L-3.80516194,11.2687247 L-3.80516194,8.31764507 L-2.32962213,8.31764507 L-2.32962213,9.79318489 L-1.59185223,9.79318489 L-1.59185223,8.31764507 L-0.116312416,8.31764507 L-0.116312416,10.5309548 L0.62145749,10.5309548 L0.62145749,8.31764507 L2.0969973,8.31764507 L2.0969973,9.79318489 L2.83476721,9.79318489 L2.83476721,8.31764507 L4.31030702,8.31764507 L4.31030702,9.79318489 L5.04807692,9.79318489 L5.04807692,8.31764507 L6.52361673,8.31764507 L6.52361673,10.5309548 L7.26138664,10.5309548 L7.26138664,8.31764507 L8.73692645,8.31764507 L8.73692645,9.79318489 L9.47469636,9.79318489 Z",
    id: "Page-1",
    transform: "translate(3.572537, 9.793185) rotate(-270.000000) translate(-3.572537, -9.793185) "
  }), h("polygon", {
    points: "12.5698348 4.03508772 10.1578947 7.17060982 11.4978614 7.17060982 11.4978614 10.3058706 11.4978614 13.441654 10.1578947 13.441654 12.5698348 16.5771761 14.9817749 13.441654 13.6418082 13.441654 13.6418082 10.3058706 13.6418082 7.17060982 14.9817749 7.17060982"
  })))))))),
  'three-quarters-protractor': h("svg", {
    width: "20px",
    height: "20px",
    viewBox: "0 0 20 20"
  }, h("path", {
    d: "M10,0C4.5,0,0,4.5,0,10s4.5,10,10,10h0.8v-9.2H20V10C20,4.5,15.5,0,10,0z M9.3,9.2v9.2 c-1.1-0.1-2.2-0.4-3.2-0.9l1.2-2.1L6.7,15l-1.2,2.1c-1-0.7-1.9-1.6-2.6-2.6L5,13.3l-0.4-0.7l-2.1,1.2c-0.6-1.1-0.9-2.3-0.9-3.5H6 V9.5H1.6C1.7,8.2,2,7,2.5,6l2.1,1.2L5,6.5L3,5.3c0.7-1,1.6-1.9,2.6-2.6l1.2,2l0.6-0.4l-1.2-2c1.1-0.6,2.3-0.9,3.5-0.9v4.4h0.8V1.4 C11.8,1.5,13,1.8,14,2.3l-1.2,2l0.6,0.4l1.2-2c1,0.7,1.9,1.6,2.6,2.6l-2,1.2l0.4,0.7l2-1.2c0.5,1,0.8,2,0.9,3.2 C18.5,9.2,9.3,9.2,9.3,9.2z"
  })),
  'vertical-line': h("svg", {
    width: "1.5px",
    height: "18.5px",
    viewBox: "0 0 1.5 18.5"
  }, h("rect", {
    x: "0",
    y: "0",
    width: "1.5",
    height: "18.5"
  })),
  'horizontal-line': h("svg", {
    width: "18.5px",
    height: "1.5px",
    viewBox: "0 0 18.5 1.5"
  }, h("rect", {
    y: "0",
    width: "18.5",
    height: "1.5"
  })),
  color: h("svg", {
    width: "44",
    height: "39",
    viewBox: "0 0 44 39",
    fill: "none"
  }, h("path", {
    d: "M0.86573 17.5153C0.33 18.4433 0.330001 19.5867 0.86573 20.5148L10.1102 36.5298C10.646 37.4581 11.6365 38.03 12.7084 38.03H31.2922C32.3641 38.03 33.3545 37.4581 33.8904 36.5298L43.1349 20.5147C43.6706 19.5867 43.6706 18.4433 43.1349 17.5152L33.8904 1.50022C33.3546 0.571893 32.3641 0 31.2922 0H12.7084C11.6365 0 10.6461 0.571894 10.1102 1.50022L0.86573 17.5153Z",
    fill: "#3D3D3D"
  }), h("path", {
    d: "M1.54024 18.0574C1.18298 18.6762 1.18298 19.4386 1.54024 20.0574L10.8898 36.2513C11.2471 36.8701 11.9073 37.2512 12.6219 37.2512H31.4128C32.1273 37.2512 32.7876 36.8701 33.1448 36.2512L42.4944 20.0573C42.8517 19.4385 42.8517 18.6761 42.4944 18.0573L33.1449 1.8634C32.7876 1.2446 32.1273 0.863403 31.4128 0.863403H12.6219C11.9073 0.863403 11.2471 1.2446 10.8898 1.86341L1.54024 18.0574Z",
    fill: "white"
  }), h("mask", {
    id: "mask0_106_2",
    style: "mask-type:alpha",
    maskUnits: "userSpaceOnUse",
    x: "3",
    y: "3",
    width: "38",
    height: "32"
  }, h("path", {
    d: "M4.22938 18.0003C3.94776 18.4882 3.80695 18.7322 3.80695 19.0001C3.80695 19.2679 3.94776 19.5119 4.22938 19.9998L12.3097 33.9998C12.5915 34.4879 12.7324 34.732 12.9644 34.866C13.1965 35 13.4783 35 14.0419 35H30.2823C30.8459 35 31.1277 35 31.3598 34.866C31.5919 34.732 31.7328 34.4879 32.0145 33.9998L40.0949 19.9997C40.3765 19.5118 40.5173 19.2678 40.5173 19C40.5173 18.7321 40.3765 18.4881 40.0949 18.0002L32.0146 4.00023C31.7328 3.51209 31.592 3.26802 31.3599 3.13401C31.1278 3 30.846 3 30.2824 3H14.042C13.4784 3 13.1966 3 12.9645 3.13401C12.7324 3.26802 12.5915 3.51209 12.3098 4.00023L4.22938 18.0003Z",
    fill: "#3D3D3D"
  })), h("g", {
    mask: "url(#mask0_106_2)"
  }, h("path", {
    d: "M1.65764 23.3821C0.887835 23.3821 0.406709 22.5488 0.791609 21.8821L7.59076 10.1057C7.97566 9.43899 8.93791 9.43899 9.32281 10.1057L16.122 21.8821C16.5069 22.5488 16.0257 23.3821 15.2559 23.3821L1.65764 23.3821Z",
    fill: "#E3216F"
  }), h("path", {
    d: "M22.6576 12.3821C21.8878 12.3821 21.4067 11.5488 21.7916 10.8821L28.5908 -0.89434C28.9757 -1.56101 29.9379 -1.56101 30.3228 -0.89434L37.122 10.8821C37.5069 11.5488 37.0257 12.3821 36.2559 12.3821L22.6576 12.3821Z",
    fill: "#34BBBB"
  }), h("path", {
    d: "M16.7169 15.2019C16.332 15.8686 15.3698 15.8686 14.9849 15.2019L8.18575 3.42544C7.80085 2.75877 8.28197 1.92543 9.05177 1.92543L22.6501 1.92543C23.4199 1.92543 23.901 2.75877 23.5161 3.42543L16.7169 15.2019Z",
    fill: "#EFC24F"
  }), h("path", {
    d: "M42.2559 14.5312C43.0257 14.5312 43.5069 15.3645 43.122 16.0312L36.3228 27.8077C35.9379 28.4743 34.9757 28.4743 34.5908 27.8077L27.7916 16.0312C27.4067 15.3645 27.8878 14.5312 28.6576 14.5312L42.2559 14.5312Z",
    fill: "#E3216F"
  }), h("path", {
    d: "M21.2559 25.5312C22.0257 25.5312 22.5069 26.3645 22.122 27.0312L15.3228 38.8077C14.9379 39.4743 13.9757 39.4743 13.5908 38.8077L6.79162 27.0312C6.40672 26.3645 6.88784 25.5312 7.65764 25.5312L21.2559 25.5312Z",
    fill: "#34BBBB"
  }), h("path", {
    d: "M26.985 22.5C27.3699 21.8333 28.3321 21.8333 28.717 22.5L35.5162 34.2765C35.9011 34.9431 35.4199 35.7765 34.6501 35.7765H21.0518C20.282 35.7765 19.8009 34.9431 20.1858 34.2765L26.985 22.5Z",
    fill: "#674893"
  })), h("circle", {
    cx: "22",
    cy: "19",
    r: "5",
    fill: "#3D3D3D"
  }), h("circle", {
    cx: "23.5",
    cy: "17.5",
    r: "1.5",
    fill: "white"
  })),
  'caret-down': h("svg", {
    width: "9",
    height: "6",
    viewBox: "0 0 9 6"
  }, h("path", {
    d: "M4.5 5.7273L9 2.35081e-05L1.00139e-06 2.19345e-05L4.5 5.7273Z"
  })),
  'caret-up': h("svg", {
    width: "9",
    height: "6",
    viewBox: "0 0 9 6"
  }, h("path", {
    d: "M4.5 3.93402e-07L5.00694e-07 5.72727L9 5.72727L4.5 3.93402e-07Z"
  })),
  'close-2': h("svg", {
    width: "10",
    height: "10",
    viewBox: "0 0 10 10"
  }, h("path", {
    d: "M8.289 9.182L5.238 6.131L2.16 9.2L0.981 8.039L4.059 4.952L1.125 2.018L2.295 0.848L5.229 3.782L8.145 0.866L9.333 2.036L6.408 4.961L9.468 8.021L8.289 9.182Z"
  }))
};

const _excluded$8 = ["icon", "className"];
const Icon = _ref => {
  let {
      icon,
      className = ''
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  const svg = library[icon];
  if (!svg) {
    throw new Error(`Icon ${icon} is not registered.`);
  }
  return cloneElement(svg, Object.assign({}, props, {
    className: `p-fill-current ${className}`,
    'aria-hidden': true,
    'data-testid': `${icon}-icon`
  }));
};

const _excluded$7 = ["icon", "label"];
const VideoControlButton = forwardRef((_ref, ref) => {
  let {
      icon,
      label
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  return h("button", _extends({}, props, {
    ref: ref,
    className: "p-w-12 p-h-12 p-z-40 p-rounded-full p-inline-flex p-justify-center p-items-center focus:p-outline-none focus:p-shadow-focus",
    type: "button",
    "aria-label": label
  }), h(Icon, {
    icon: icon
  }));
});
VideoControlButton.displayName = 'VideoControlButton';

const VideoControls = () => {
  const {
    isSmallScreen,
    fullScreenEnabled,
    isFullScreen,
    toggleFullScreen
  } = useFullScreen();
  const {
    dispatch,
    isPlaying
  } = usePlayback();
  return isSmallScreen ? null : h("div", {
    className: "p-flex p-w-full p-z-30",
    role: "group",
    "aria-label": "Video Playback"
  }, h(VideoControlButton, {
    label: "Restart",
    icon: "restart",
    onClick: () => dispatch({
      type: 'RESTART'
    })
  }), h(VideoControlButton, {
    label: "Previous Frame",
    icon: "backward",
    onClick: () => dispatch({
      type: 'PREV'
    })
  }), h(VideoControlButton, {
    label: isPlaying ? 'Pause' : 'Play',
    icon: isPlaying ? 'pause' : 'play',
    onClick: () => dispatch({
      type: 'TOGGLE'
    })
  }), h(VideoControlButton, {
    label: "Next Frame",
    icon: "forward",
    onClick: () => dispatch({
      type: 'NEXT'
    })
  }), h(SeekBar, null), fullScreenEnabled && h(VideoControlButton, {
    label: `${isFullScreen ? 'Exit' : 'Enter'} Fullscreen`,
    icon: isFullScreen ? 'fullscreen' : 'not-fullscreen',
    onClick: toggleFullScreen
  }));
};

const ASPECT = 16 / 9;
const VideoOverlay = ({
  children
}) => {
  const {
    isSmallScreen
  } = useFullScreen();
  const {
    width,
    height,
    setSize,
    videoAspect
  } = useOverlayDimensions();
  const {
    width: containerWidth,
    height: containerHeight,
    ref
  } = useMeasuredElement();
  useLayoutEffect(() => {
    // Determine video dimensions from video aspect and container dimensions
    let videoWidth = containerWidth;
    let videoHeight = containerHeight;
    const containerAspect = containerWidth / containerHeight;
    if (containerAspect < videoAspect) {
      videoHeight = containerWidth / videoAspect;
    } else {
      videoWidth = containerHeight * videoAspect;
    }
    // Size the overlay dimensions to fit around the video dimensions.
    let overlayWidth = videoWidth;
    let overlayHeight = videoHeight;
    if (ASPECT < videoAspect) {
      overlayHeight = videoWidth / ASPECT;
    } else {
      overlayWidth = videoHeight * ASPECT;
    }
    setSize({
      width: overlayWidth,
      height: overlayHeight
    });
  }, [containerHeight, containerWidth, setSize, videoAspect]);
  return isSmallScreen ? null : h("div", {
    ref: ref,
    className: "p-absolute p-inset-0 p-flex p-justify-center p-items-center p-overflow-hidden"
  }, h(PointerBounds, null, h("div", {
    className: "p-absolute",
    style: {
      width,
      height
    }
  }, children)));
};

let idCounter = 0;
// Generate a random id for a component.
// This is useful for making random id selectors for reusable components.
function useComponentId() {
  const [id] = useState(() => idCounter++);
  return id;
}

const _excluded$6 = ["children", "className"];
const ToolbarBaseButton = _ref => {
  let {
      children,
      className
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  return h("button", _extends({}, props, {
    type: "button",
    className: `
        p-relative p-w-12 p-h-12 p-bg-black p-text-white
        p-rounded-full p-border-2 p-border-white
        p-flex p-items-center p-justify-center p-z-30
        focus:p-outline-none focus:p-shadow-focus
        ${className || ''}
      `,
    onKeyDown: e => {
      if (e.code === 'Space') {
        // This prevents other event handlers processing space
        // and allows space to be treated as a click.
        e.stopPropagation();
      }
    }
  }), children);
};

const ToolbarToolButton = ({
  className: _className = '',
  limit,
  count,
  label,
  icon,
  src,
  onClick,
  onFocus,
  tabIndex
}) => {
  const id = useComponentId();
  const descriptionId = `tool-description-${id}`;
  const isAdd = !limit || limit > 1;
  const isDisabled = isAdd && limit === count;
  return h(ToolbarBaseButton, {
    className: `
          ${isDisabled ? 'p-opacity-75' : ''}
          ${_className}
        `,
    tabIndex: tabIndex,
    "aria-label": `${isAdd || count === 0 ? 'Add' : 'Remove'} ${label}`,
    onClick: () => onClick && !isDisabled && onClick(),
    onFocus: onFocus,
    "aria-disabled": isDisabled,
    "aria-describedby": descriptionId,
    title: label
  }, icon ? h(Icon, {
    icon: icon,
    className: "p-w-6 p-h-6"
  }) : h("img", {
    "aria-hidden": true,
    src: src,
    className: "p-w-6 p-h-6 p-bg-black",
    draggable: false
  }), h("div", {
    className: "p-sr-only",
    id: descriptionId
  }, count, " of ", limit || 'infinite', " used"), isAdd && h("div", {
    className: "p-absolute p-bottom-0 p-right-0 p-text-xs p-font-bold p-w-5 p-h-5 p--mb-1 p--mr-1 p-border-2 p-border-white p-rounded-full p-bg-white p-text-black",
    "aria-hidden": true
  }, limit ? limit - count : '∞'));
};

const Toolbar = () => {
  const componentId = useComponentId();
  const {
    isSmallScreen,
    isNarrowScreen
  } = useFullScreen();
  const {
    types,
    dispatch
  } = useToolTypes();
  const [isExpanded, setExpanded] = useState(false);
  const dropdownId = `tool-menu-${componentId}`;
  return types.length > 0 && !isSmallScreen ? h("div", {
    className: `
        p-absolute p-right-0 p-top-0 p-m-4
        ${isNarrowScreen ? '' : 'p-flex p-flex-row-reverse'}
      `,
    onKeyDown: e => {
      if (e.code === 'Escape') {
        e.stopPropagation();
        setExpanded(false);
      }
    }
  }, h(ToolbarBaseButton, {
    className: isNarrowScreen ? 'p-mb-2' : 'p-ml-2',
    onClick: () => setExpanded(isExpanded => !isExpanded),
    "aria-label": "Toggle Tools",
    "aria-haspopup": "true",
    "aria-expanded": isExpanded,
    "aria-controls": dropdownId
  }, h(Icon, {
    icon: "tools",
    className: "p-w-5 p-h-5"
  })), isExpanded && h("div", {
    className: isNarrowScreen ? '' : 'p-flex p-flex-row-reverse',
    id: dropdownId,
    role: "group",
    "aria-label": "Tools"
  }, types.map(type => h(ToolbarToolButton, {
    className: isNarrowScreen ? 'p-mb-2' : 'p-ml-2',
    limit: type.limit,
    count: type.count,
    icon: type.icon,
    src: type.iconSrc,
    label: type.label,
    onClick: () => dispatch({
      action: type.limit === 1 ? 'TOGGLE' : 'ADD',
      typeId: type.id
    })
  })))) : null;
};

var ToolType;
(function (ToolType) {
  ToolType["Calibration"] = "calibration";
  ToolType["Stopwatch"] = "stopwatch";
  ToolType["Image"] = "image";
  ToolType["Ruler"] = "ruler";
  ToolType["Grid"] = "grid";
  ToolType["Protractor"] = "protractor";
  ToolType["Color"] = "color";
  ToolType["ColorReadout"] = "color-readout";
})(ToolType || (ToolType = {}));
var PrecisionType;
(function (PrecisionType) {
  PrecisionType["Decimals"] = "decimals";
  PrecisionType["SigFigs"] = "sigfigs";
})(PrecisionType || (PrecisionType = {}));

function toStopwatchConfig(settings) {
  if (settings) {
    var _settings$x, _settings$y, _settings$precision, _settings$units;
    settings = typeof settings === 'boolean' ? {} : settings;
    return {
      type: ToolType.Stopwatch,
      limit: settings.limit || undefined,
      icon: 'stopwatch',
      label: 'Stopwatch',
      x: (_settings$x = settings.x) != null ? _settings$x : 0.5,
      y: (_settings$y = settings.y) != null ? _settings$y : 0.5,
      precision: (_settings$precision = settings.precision) != null ? _settings$precision : 4,
      units: (_settings$units = settings.units) != null ? _settings$units : 'seconds',
      preventReset: settings.preventReset ? settings.preventReset : false
    };
  }
  return;
}

var palettes = {
  ironbow: ['000000', '000024', '000033', '000042', '000051', '02005a', '040063', '07006a', '0b0073', '0e0077', '14007b', '1b0080', '210085', '290089', '30008c', '37008f', '3d0092', '420095', '480096', '4e0097', '540098', '5b0099', '61009b', '68009b', '6e009c', '73009d', '7a009d', '80009d', '86009d', '8b009d', '92009c', '98009b', '9d009b', 'a2009b', 'a7009a', 'ab0099', 'af0198', 'b20197', 'b60295', 'b90495', 'bc0593', 'bf0692', 'c10890', 'c30b8e', 'c60d8b', 'c91187', 'cb1484', 'ce177f', 'd01a79', 'd21d74', 'd4216f', 'd62567', 'd92961', 'db2e59', 'dd314e', 'df3542', 'e03836', 'e23c2a', 'e4401e', 'e54419', 'e74814', 'e84c10', 'ea4e0c', 'eb520a', 'ec5608', 'ed5a07', 'ee5d05', 'ef6004', 'f06403', 'f16703', 'f16a02', 'f26d01', 'f37101', 'f47400', 'f47800', 'f57d00', 'f68100', 'f78500', 'f88800', 'f88b00', 'f98e00', 'f99100', 'fa9500', 'fb9a00', 'fc9f00', 'fda300', 'fda800', 'fdac00', 'feb000', 'feb300', 'feb800', 'febb00', 'febf00', 'fec300', 'fec700', 'feca01', 'fecd02', 'fed005', 'fed409', 'fed80c', 'ffdb0f', 'ffdd17', 'ffe020', 'ffe327', 'ffe532', 'ffe83f', 'ffeb4b', 'ffee58', 'ffef66', 'fff174', 'fff286', 'fff495', 'fff5a4', 'fff7b3', 'fff8c0', 'fff9cb', 'fffbd8', 'fffde4', 'fffeef', 'fffff9']
};

function resolvePalette(config) {
  let steps = config.steps;
  if (!steps && config.name) {
    steps = palettes[config.name];
  }
  if (steps) {
    var _config$min, _config$max;
    const length = steps.length - 1;
    const min = (_config$min = config.min) != null ? _config$min : 0;
    const max = (_config$max = config.max) != null ? _config$max : 1;
    const normalizedSteps = steps
    // Palettes can either be a list of colors mapped to a range defined by min and max,
    // or a list of colors and measurements that may be non-linear.
    // We normalize these to a list of color, measurement pairs.
    .map((step, i) => {
      const color = Array.isArray(step) ? step[0] : step;
      const value = Array.isArray(step) ? step[1] : min + i / length * (max - min);
      return [[parseInt(color.slice(0, 2), 16), parseInt(color.slice(2, 4), 16), parseInt(color.slice(4, 6), 16)], value];
    });
    // Now, if a precision is specified, we interpolate color, measurement pairs between each step in the palette
    // so that there are sufficient steps in the palette to measure to the specified precision
    if (config.precision) {
      var _config$obscureExtrem;
      const {
        steps: interpolatedSteps
      } = normalizedSteps.reduce(({
        prev,
        steps
      }, step) => {
        if (prev) {
          var _config$precision;
          const colorDiff = [step[0][0] - prev[0][0], step[0][1] - prev[0][1], step[0][2] - prev[0][2]];
          const valueDiff = step[1] - prev[1];
          const stepCount = Math.ceil(valueDiff / ((_config$precision = config.precision) != null ? _config$precision : 0.1));
          steps = steps.concat(Array.from({
            length: stepCount
          }).map((_, i) => [[prev[0][0] + i / stepCount * colorDiff[0], prev[0][1] + i / stepCount * colorDiff[1], prev[0][2] + i / stepCount * colorDiff[2]], prev[1] + i / stepCount * valueDiff]));
        }
        return {
          prev: step,
          steps
        };
      }, {
        steps: []
      });
      return {
        points: interpolatedSteps,
        obscureExtremes: (_config$obscureExtrem = config.obscureExtremes) != null ? _config$obscureExtrem : config.name === 'ironbow'
      };
    } else {
      var _config$obscureExtrem2;
      return {
        points: normalizedSteps,
        obscureExtremes: (_config$obscureExtrem2 = config.obscureExtremes) != null ? _config$obscureExtrem2 : config.name === 'ironbow'
      };
    }
  }
  return;
}
function mapColorToPalette(color, palette) {
  // We pick a color from the palette by minimizing the distance between the measured color and the colors in the palette.
  const results = palette.points.map(([paletteColor, value]) => {
    const dist = Math.sqrt(Math.pow(color[0] - paletteColor[0], 2) + Math.pow(color[1] - paletteColor[1], 2) + Math.pow(color[2] - paletteColor[2], 2));
    return {
      dist,
      color: paletteColor,
      value
    };
  }).sort((a, b) => a.dist - b.dist);
  if (palette.obscureExtremes) {
    switch (results[0].value) {
      case palette.points[0][1]:
        return {
          value: results[0].value,
          type: 'min'
        };
      case palette.points[palette.points.length - 1][1]:
        return {
          value: results[0].value,
          type: 'max'
        };
      default:
        return {
          value: results[0].value
        };
    }
  } else {
    return {
      value: results[0].value
    };
  }
}
function getPixel(frameData, x, y) {
  const i = 3 * (y * frameData.width + x);
  return [frameData.data[i], frameData.data[i + 1], frameData.data[i + 2]];
}
function getColorSample(frameData, x, y, r, sampleMethod) {
  const videoAspect = frameData.width / frameData.height;
  const containerAspect = 16 / 9;
  const containerWidth = 1;
  const containerHeight = containerWidth / containerAspect;
  // The container has a consistent aspect ratio, while the video is variable.
  // This means, the video either fills either the full width or full height or both.
  // The other dimension is scaled according to the video aspect ratio.
  let videoWidth = containerWidth;
  let videoHeight = containerHeight;
  if (containerAspect < videoAspect) {
    videoHeight = containerWidth / videoAspect;
  } else {
    videoWidth = containerHeight * videoAspect;
  }
  // Sample position is reported in relative coordinates ([0, 1] for both dimensions)
  // This means we have to get the ratio of the video dimensions to the container.
  const videoWidthRatio = videoWidth / containerWidth;
  const videoHeightRatio = videoHeight / containerHeight;
  // Then get the dimensional offset because the video is always centered.
  const videoHorizontalOffset = (1 - videoWidthRatio) / 2;
  const videoVerticalOffset = (1 - videoHeightRatio) / 2;
  // Then we can get the relative coordiante in the video ([0, 1] for both dimensions)
  const xv = (x - videoHorizontalOffset) / videoWidthRatio;
  const yv = (y - videoVerticalOffset) / videoHeightRatio;
  // This protects us from searching for color data outside the bounds of the video.
  if (xv < 0 || xv > 1 || yv < 0 || yv > 1) return [0, 0, 0];
  // Convert the relative location to a pixel value.
  const xc = Math.round(frameData.width * xv);
  const yc = Math.round(frameData.height * yv);
  const rsq = r * r;
  function isInCircle(x, y) {
    return x >= 0 && x < frameData.width && y >= 0 && y <= frameData.height && (x - xc) ** 2 + (y - yc) ** 2 <= rsq;
  }
  const pixels = [];
  for (let i = xc - r; i <= xc + r; i++) {
    for (let j = yc - r; j <= yc + r; j++) {
      if (isInCircle(i, j)) {
        pixels.push(getPixel(frameData, i, j));
      }
    }
  }
  switch (sampleMethod) {
    case 'avg':
      {
        // See this article for why we avg the color this way:
        // https://sighack.com/post/averaging-rgb-colors-the-right-way
        const count = pixels.length;
        const sample = pixels.reduce((sample, pixel) => [sample[0] + pixel[0] ** 2, sample[1] + pixel[1] ** 2, sample[2] + pixel[2] ** 2], [0, 0, 0]);
        return [Math.sqrt(sample[0] / count), Math.sqrt(sample[1] / count), Math.sqrt(sample[2] / count)];
      }
    case 'sum':
      {
        return pixels.reduce((sample, pixel) => [sample[0] + pixel[0], sample[1] + pixel[1], sample[2] + pixel[2]], [0, 0, 0]);
      }
    default:
      {
        throw new Error(`Sample method '${sampleMethod}' is not supported.`);
      }
  }
}
function formatPixelValue(value) {
  return Math.round(value).toString().padStart(3, '0');
}
function getColorReading(options) {
  const {
    frameData,
    x,
    y,
    r,
    palette,
    formula,
    precision,
    sampleMethod
  } = options;
  let sample = getColorSample(frameData, x, y, r, sampleMethod);
  let paletteValue;
  let paletteType;
  if (palette) {
    const paletteData = mapColorToPalette(sample, palette);
    paletteValue = paletteData.value;
    paletteType = paletteData.type;
  }
  let reading = '';
  if (formula) {
    const hsl = rgbToHsl(sample);
    const value = math.evaluate(formula, {
      red: sample[0],
      green: sample[1],
      blue: sample[2],
      hue: hsl[0],
      saturation: hsl[1],
      lightness: hsl[2],
      wavelength: hslToWavelength(hsl),
      blackbody: rgbToBlackbody(sample),
      palette: paletteValue
    });
    if (typeof value === 'string') {
      reading = value;
    } else if (options.precisionType === PrecisionType.Decimals) {
      reading = value.toFixed(precision);
    } else {
      reading = value.toPrecision(precision);
    }
  } else if (palette) {
    if (options.precisionType === PrecisionType.Decimals) {
      var _paletteValue$toFixed, _paletteValue;
      reading = (_paletteValue$toFixed = (_paletteValue = paletteValue) == null ? void 0 : _paletteValue.toFixed(precision)) != null ? _paletteValue$toFixed : '';
    } else {
      var _paletteValue$toPreci, _paletteValue2;
      reading = (_paletteValue$toPreci = (_paletteValue2 = paletteValue) == null ? void 0 : _paletteValue2.toPrecision(precision)) != null ? _paletteValue$toPreci : '';
    }
  } else {
    reading = `R: ${formatPixelValue(sample[0])} G: ${formatPixelValue(sample[1])} B: ${formatPixelValue(sample[2])}`;
  }
  if (sampleMethod !== 'avg') {
    sample = getColorSample(frameData, x, y, r, 'avg');
  }
  return {
    reading,
    color: sample,
    type: paletteType
  };
}
function getAvgColor(frameData) {
  const sample = [0, 0, 0];
  for (let i = 0; i < frameData.width; i++) {
    for (let j = 0; j < frameData.height; j++) {
      const pixel = getPixel(frameData, i, j);
      sample[0] += pixel[0] ** 2;
      sample[1] += pixel[1] ** 2;
      sample[2] += pixel[2] ** 2;
    }
  }
  const count = frameData.width * frameData.height;
  return (Math.sqrt(sample[0] / count) + Math.sqrt(sample[1] / count) + Math.sqrt(sample[2] / count)) / 3;
}
function rgbToHsl(rgb) {
  const red = rgb[0] / 255;
  const green = rgb[1] / 255;
  const blue = rgb[2] / 255;
  const cMax = Math.max(red, green, blue);
  const cMin = Math.min(red, green, blue);
  const delta = cMax - cMin;
  let hue = 0;
  if (delta != 0) {
    switch (cMax) {
      case red:
        {
          hue = 60 * ((green - blue) / delta + (blue > green ? 6 : 0));
          break;
        }
      case green:
        {
          hue = 60 * ((blue - red) / delta + 2);
          break;
        }
      case blue:
        {
          hue = 60 * ((red - green) / delta + 4);
          break;
        }
    }
  }
  const lightness = (cMax + cMin) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  return [hue, saturation, lightness];
}
function rgbSToC(channel) {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}
function rgbToXyx(rgb) {
  const r = rgbSToC(rgb[0] / 255);
  const g = rgbSToC(rgb[1] / 255);
  const b = rgbSToC(rgb[2] / 255);
  return [0.4124 * r + 0.3576 * g + 0.1805 * b, 0.2126 * r + 0.7152 * g + 0.0722 * b, 0.0193 * r + 0.1192 * g + 0.9505 * b];
}
function hslToWavelength(hsl) {
  return 650 - 0.96296 * hsl[0];
}
function rgbToBlackbody(rgb) {
  const [x, y, z] = rgbToXyx(rgb);
  const sum = x + y + z;
  const a = x / sum;
  const b = y / sum;
  const n = (a - 0.3366) / (b - 0.1735);
  return -949.86315 + 6253.80338 * Math.exp(-n / 0.92159) + 28.70599 * Math.exp(-n / 0.20039) + 0.00004 * Math.exp(-n / 0.07125);
}

function toToolConfig(settings) {
  var _settings$draggable, _settings$rotatable, _settings$resizable, _settings$x, _settings$y, _settings$t, _settings$width, _settings$height, _settings$width2, _settings$height2, _settings$r, _settings$p, _settings$label, _settings$r2, _settings$sampleMetho, _settings$precision, _settings$precisionTy, _settings$resizable2, _settings$width3, _settings$height3, _settings$lockAspect;
  if (settings.type === 'color-readout') {
    throw new Error('stop trying to turn the color readout into a tool');
  }
  const commonSettings = {
    limit: settings.limit || undefined,
    draggable: (_settings$draggable = settings.draggable) != null ? _settings$draggable : true,
    rotatable: (_settings$rotatable = settings.rotatable) != null ? _settings$rotatable : true,
    resizable: (_settings$resizable = settings.resizable) != null ? _settings$resizable : true,
    x: (_settings$x = settings.x) != null ? _settings$x : 0.5,
    y: (_settings$y = settings.y) != null ? _settings$y : 0.5,
    t: (_settings$t = settings.t) != null ? _settings$t : 0,
    bounds: settings.bounds,
    iconSrc: settings.iconSrc
  };
  switch (settings.type) {
    case 'ruler':
      return Object.assign({}, commonSettings, {
        w: (_settings$width = settings.width) != null ? _settings$width : 0.2,
        h: (_settings$height = settings.height) != null ? _settings$height : 0.2,
        type: ToolType.Ruler,
        label: 'Ruler'
      }, !commonSettings.iconSrc ? {
        icon: 'horizontal-ruler'
      } : {});
    case 'grid':
      return Object.assign({}, commonSettings, {
        w: (_settings$width2 = settings.width) != null ? _settings$width2 : 0.2,
        h: (_settings$height2 = settings.height) != null ? _settings$height2 : 0.2,
        type: ToolType.Grid,
        label: 'Grid'
      }, !commonSettings.iconSrc ? {
        icon: 'grid'
      } : {});
    case 'protractor':
      return Object.assign({}, commonSettings, {
        type: ToolType.Protractor,
        label: 'Protractor'
      }, !commonSettings.iconSrc ? {
        icon: 'protractor'
      } : {}, {
        r: (_settings$r = settings.r) != null ? _settings$r : 0.2,
        p: -((_settings$p = settings.p) != null ? _settings$p : Math.PI)
      });
    case 'color':
      return Object.assign({}, commonSettings, {
        label: (_settings$label = settings.label) != null ? _settings$label : 'Iris',
        limit: 1,
        type: ToolType.Color
      }, !commonSettings.iconSrc ? {
        icon: 'color'
      } : {}, {
        r: (_settings$r2 = settings.r) != null ? _settings$r2 : 1,
        formula: settings.formula,
        sampleMethod: (_settings$sampleMetho = settings.sampleMethod) != null ? _settings$sampleMetho : 'avg',
        precision: (_settings$precision = settings.precision) != null ? _settings$precision : 2,
        precisionType: (_settings$precisionTy = settings.precisionType) != null ? _settings$precisionTy : PrecisionType.Decimals,
        units: settings.units,
        resizable: (_settings$resizable2 = settings.resizable) != null ? _settings$resizable2 : false
      }, settings.palette && {
        palette: resolvePalette(settings.palette)
      });
    default:
    case 'image':
      return Object.assign({}, commonSettings, {
        w: (_settings$width3 = settings.width) != null ? _settings$width3 : 0.2,
        h: (_settings$height3 = settings.height) != null ? _settings$height3 : 0.2,
        type: ToolType.Image,
        src: settings.src,
        label: settings.label,
        icon: settings.icon,
        lockAspect: (_settings$lockAspect = settings.lockAspect) != null ? _settings$lockAspect : true
      });
  }
}

function toSelectionConfig(settings, videoSettings) {
  // Filters out old settings which allowed boolean values
  if (typeof settings === 'object') {
    var _settings$enableIndep;
    return {
      enableIndependence: (_settings$enableIndep = settings.enableIndependence) != null ? _settings$enableIndep : false,
      parameters: Object.entries(settings.parameters).map(([name, {
        title,
        initial,
        selectable,
        visible
      }]) => {
        var _videoSettings$0$para;
        return {
          name,
          title: title != null ? title : name,
          initial: (initial != null ? initial : ((_videoSettings$0$para = videoSettings[0].parameters) != null ? _videoSettings$0$para : {})[name]).toString(),
          selectable: selectable != null ? selectable : true,
          visible: selectable === false ? visible != null ? visible : true : true
        };
      })
    };
  }
  return;
}

function toVideoConfig(settings, selectionSettings) {
  var _selectionSettings$pa, _settings$parameters;
  const expectedParameters = Object.keys((_selectionSettings$pa = selectionSettings == null ? void 0 : selectionSettings.parameters) != null ? _selectionSettings$pa : {});
  const actualParameters = (_settings$parameters = settings.parameters) != null ? _settings$parameters : {};
  // We need to convert all parameters to strings to make comparisons with the initial value consistent.
  const parameters = Object.fromEntries(expectedParameters.map(key => {
    if ([undefined, null].includes(actualParameters[key])) {
      throw new Error('video settings are missing a parameter');
    }
    return [key, actualParameters[key].toString()];
  }));
  return Object.assign({}, settings, {
    parameters
  });
}

function toCalibrationConfig(settings) {
  if (settings) {
    var _settings$left, _settings$right, _settings$color, _settings$editable;
    settings = typeof settings === 'boolean' ? {} : settings;
    const config = {
      left: (_settings$left = settings.left) != null ? _settings$left : {
        x: 0.3,
        y: 0.5
      },
      right: (_settings$right = settings.right) != null ? _settings$right : {
        x: 0.7,
        y: 0.5
      },
      distance: settings.distance,
      units: settings.units || '',
      color: (_settings$color = settings.color) != null ? _settings$color : 'green'
    };
    if ((_settings$editable = settings.editable) != null ? _settings$editable : false) {
      return {
        tool: {
          type: ToolType.Calibration,
          icon: 'calibration',
          label: 'Calibration',
          limit: 1
        },
        config
      };
    } else {
      return {
        config
      };
    }
  }
  return;
}

function useSettings(options) {
  const {
    settings
  } = options;
  return useMemo(() => {
    var _settings$tools;
    const tools = [];
    let videos = (settings.videos || []).map(video => toVideoConfig(video, settings.selection));
    const selection = toSelectionConfig(settings.selection, settings.videos);
    const lockedParameters = selection == null ? void 0 : selection.parameters.filter(param => !param.selectable);
    if (lockedParameters) {
      videos = videos.filter(video => lockedParameters.every(param => video.parameters[param.name] === param.initial.toString()));
    }
    const calibration = toCalibrationConfig(settings.calibration);
    // If calibration is editable, add it as a tool.
    if (calibration && calibration.tool) {
      tools.push(calibration.tool);
    }
    const stopwatch = toStopwatchConfig(settings.stopwatch);
    if (stopwatch) {
      tools.push(stopwatch);
    }
    // Calibration and stopwatch are always the first tools if enabled
    // so we push the tools to the end of the list.
    tools.push(...((_settings$tools = settings.tools) != null ? _settings$tools : []).filter(tool => tool.type !== 'color-readout').map(toToolConfig));
    // Add color readout if none supplied.
    let colorReadout;
    if (tools.some(tool => tool.type === ToolType.Color)) {
      var _settings$tools2, _readoutSettings$x, _readoutSettings$y, _readoutSettings$drag;
      const readoutSettings = (_settings$tools2 = settings.tools) == null ? void 0 : _settings$tools2.find(tool => tool.type === 'color-readout');
      colorReadout = {
        x: (_readoutSettings$x = readoutSettings == null ? void 0 : readoutSettings.x) != null ? _readoutSettings$x : 0.5,
        y: (_readoutSettings$y = readoutSettings == null ? void 0 : readoutSettings.y) != null ? _readoutSettings$y : 0.5,
        draggable: (_readoutSettings$drag = readoutSettings == null ? void 0 : readoutSettings.draggable) != null ? _readoutSettings$drag : true,
        bounds: readoutSettings == null ? void 0 : readoutSettings.bounds
      };
    }
    return {
      videos,
      selection,
      calibration: calibration == null ? void 0 : calibration.config,
      tools,
      colorReadout
    };
  }, [settings]);
}

const Player = ({
  children,
  preview
}) => {
  const id = useComponentId();
  const {
    isFullScreen
  } = useFullScreen();
  const {
    dispatch: playbackDispatch
  } = usePlayback();
  const {
    dispatch: selectionDispatch
  } = useSelection();
  const video = useCurrentVideo();
  const descriptionId = `pivot-player-${id}`;
  return h("div", {
    className: `
        p-relative focus:p-outline-none
        ${isFullScreen ? 'p-w-full p-h-full p-flex p-flex-col' : ''}
      `,
    role: "group",
    "aria-label": "Pivot Player",
    "data-testid": "player",
    tabIndex: -1,
    onKeyDown: e => {
      let isHandled = true;
      switch (e.code) {
        case 'Escape':
          selectionDispatch({
            type: 'CANCEL'
          });
          break;
        case 'ArrowUp':
        case 'ArrowRight':
          playbackDispatch({
            type: 'NEXT'
          });
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          playbackDispatch({
            type: 'PREV'
          });
          break;
        case 'Space':
          playbackDispatch({
            type: 'TOGGLE'
          });
          break;
        default:
          isHandled = false;
      }
      if (isHandled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    "aria-describedby": descriptionId
  }, h("div", {
    id: descriptionId,
    hidden: true
  }, preview ? 'Player is in preview mode so the video is not loaded.' : video ? video.alt : 'Configuration has no video to play.'), children);
};

const MIN_PIXELS = 4;
const INPUT_REGEX = /^(\d+(?:\.\d+)?|\.\d+) ?(.+)?$/;
const DEFAULT_LEFT = {
  x: 0.2,
  y: 0.5
};
const DEFAULT_RIGHT = {
  x: 0.8,
  y: 0.5
};
const CalibrationContext = createContext(null);
// Manages global calibration state.
// Rulers, grids, and protractors use this state to render correctly.
const CalibrationProvider = ({
  config,
  value,
  onCalibrationChange,
  children
}) => {
  var _config$left, _config$right, _config$distance, _config$units;
  const isFirstRender = useRef(true);
  const {
    width,
    height
  } = useOverlayDimensions();
  const [{
    left,
    right
  }, setPosition] = useState({
    left: (_config$left = config == null ? void 0 : config.left) != null ? _config$left : DEFAULT_LEFT,
    right: (_config$right = config == null ? void 0 : config.right) != null ? _config$right : DEFAULT_RIGHT
  });
  const [{
    text,
    distance,
    units,
    inputError
  }, setDistanceAndUnits] = useState({
    text: `${(_config$distance = config == null ? void 0 : config.distance) != null ? _config$distance : ''}${config != null && config.units ? ` ${config == null ? void 0 : config.units}` : ''}`,
    distance: config == null ? void 0 : config.distance,
    units: (_config$units = config == null ? void 0 : config.units) != null ? _config$units : '',
    inputError: false
  });
  const [color, setColor] = useState((config == null ? void 0 : config.color) || 'green');
  // Raise onCalibrationChange when data changes,
  // but only after the first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      onCalibrationChange && onCalibrationChange({
        left,
        right,
        distance,
        units,
        color
      });
    }
  }, [onCalibrationChange, left, right, distance, units, color]);
  // Calculate the width of a pixel.
  const pixelDistance = Math.sqrt((width * (left.x - right.x)) ** 2 + (height * (left.y - right.y)) ** 2);
  const pixelWidth = pixelDistance === 0 ? 0 : (distance || 0) / pixelDistance;
  // Calculate the pixel spacing between minor lines on grids and rulers.
  let spacing = 0;
  let exp = parseInt(pixelWidth.toExponential(0).split('e')[1]);
  do {
    spacing = 10 ** exp++ / pixelWidth;
  } while (spacing < MIN_PIXELS);
  // Calculate the measured distance between major increments.
  const majorIncrement = 10 ** exp;
  // State mutation methods.
  const setLeftPoint = useCallback(left => {
    setPosition(({
      right
    }) => ({
      right,
      left
    }));
  }, []);
  const setRightPoint = useCallback(right => {
    setPosition(({
      left
    }) => ({
      right,
      left
    }));
  }, []);
  // Parse the input into distance and units
  // and check for errors in the input.
  const setDistance = useCallback(input => {
    if (input) {
      const match = input.match(INPUT_REGEX);
      if (match && match[1] !== '0') {
        var _match$;
        setDistanceAndUnits({
          text: input,
          distance: Number.parseFloat(match[1]),
          units: (_match$ = match[2]) != null ? _match$ : '',
          inputError: false
        });
      } else {
        setDistanceAndUnits({
          text: input,
          units: '',
          inputError: true,
          distance: undefined
        });
      }
    } else {
      setDistanceAndUnits({
        text: '',
        units: '',
        inputError: false,
        distance: undefined
      });
    }
  }, []);
  const reset = useCallback(() => {
    setPosition({
      left: DEFAULT_LEFT,
      right: DEFAULT_RIGHT
    });
    setDistance('');
  }, [setDistance]);
  const contextValue = useMemo(() => ({
    spacing,
    majorIncrement,
    left,
    right,
    text,
    distance,
    units,
    inputError,
    color,
    setColor,
    setLeftPoint,
    setRightPoint,
    setDistance,
    reset
  }), [left, right, text, distance, units, spacing, majorIncrement, inputError, color, setColor, setLeftPoint, setRightPoint, setDistance, reset]);
  return h(CalibrationContext.Provider, {
    value: value || contextValue,
    children: children
  });
};
function useCalibration() {
  const context = useContext(CalibrationContext);
  if (!context) {
    throw new Error('useCalibration must be a child of CalibrationProvider.');
  }
  return context;
}

const _excluded$5 = ["onMouseDown", "onTouchStart", "onTouchMove", "onTouchEnd", "children"];
const ToolButton = _ref => {
  let {
      onMouseDown,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  return h("button", _extends({}, props, {
    type: "button",
    onMouseDown: e => {
      e.stopPropagation();
      // We have to cast this to never because of this bug in preact:
      // https://github.com/preactjs/preact/issues/3137
      onMouseDown && onMouseDown.call(e.currentTarget, e);
    },
    onTouchStart: e => {
      e.stopPropagation();
      onTouchStart && onTouchStart.call(e.currentTarget, e);
    },
    onTouchMove: e => {
      e.stopPropagation();
      onTouchMove && onTouchMove.call(e.currentTarget, e);
    },
    onTouchEnd: e => {
      e.stopPropagation();
      onTouchEnd && onTouchEnd.call(e.currentTarget, e);
    }
  }), children);
};

const CloseButton = ({
  dispatch: toolsDispatch,
  className,
  label,
  offset: _offset = {
    x: 0,
    y: 0
  },
  id
}) => {
  return h(ToolButton, {
    className: `
        p-group p-absolute p-text-white focus:p-outline-none
        ${className || ''}
      `,
    style: {
      transform: `translate(${_offset.x}px, ${_offset.y}px)`
    },
    "aria-label": `Close ${label}`,
    onClick: e => {
      // We have to blur this so tool components don't try to rerender
      // after the tool is removed from the DOM.
      e.currentTarget.blur();
      toolsDispatch({
        action: 'REMOVE',
        id
      });
    }
  }, h("div", {
    className: "p-w-4 p-h-4 p-m-4 p-rounded-full group-focus:p-shadow-focus p-bg-shadow"
  }, h(Icon, {
    icon: "close",
    className: "p-w-full p-h-full"
  })));
};

// Simple wrapper for use state.
// Supplies defaults for tools that don't use all parameters.
function useTransform({
  x = 0.5,
  y = 0.5,
  w = 0.7,
  h = 0.2,
  t = 0,
  p = 0
}) {
  return useState({
    x,
    y,
    w,
    h,
    t,
    p
  });
}

// Workaround for lack of support for focusin and focusout events.
// isFocused is true as long as elements inside the root element are focused.
function useFocusIn() {
  const [isFocused, setFocus] = useState(false);
  return {
    isFocused,
    // Spread these event handlers onto the root element that you want to capture focus on.
    focusProps: {
      onFocusCapture: useCallback(() => setFocus(true), []),
      onBlurCapture: useCallback(e => setFocus(
      // Stay focused if the element being focused is a child of the root element.
      isFocused => !!(isFocused && e.relatedTarget && e.currentTarget.contains(e.relatedTarget))), [])
    }
  };
}

var ResizeAnchor;
(function (ResizeAnchor) {
  ResizeAnchor["TopRight"] = "tr";
  ResizeAnchor["TopLeft"] = "tl";
  ResizeAnchor["BottomRight"] = "br";
  ResizeAnchor["BottomLeft"] = "bl";
  ResizeAnchor["Center"] = "c";
})(ResizeAnchor || (ResizeAnchor = {}));
const anchorDirection = {
  [ResizeAnchor.TopRight]: {
    x: -1,
    y: 1
  },
  [ResizeAnchor.TopLeft]: {
    x: 1,
    y: 1
  },
  [ResizeAnchor.BottomRight]: {
    x: -1,
    y: -1
  },
  [ResizeAnchor.BottomLeft]: {
    x: 1,
    y: -1
  },
  [ResizeAnchor.Center]: {
    x: 0,
    y: 0
  }
};
function toVector(p1, p2) {
  return {
    x: p2.x - p1.x,
    y: p2.y - p1.y
  };
}
function distance(p1, p2) {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
}
function vectorDirection(v) {
  return Math.atan2(v.y, v.x);
}
function vectorLength(v) {
  return Math.sqrt(v.x ** 2 + v.y ** 2);
}
function scaleVector(v, s) {
  return typeof s === 'number' ? {
    x: v.x * s,
    y: v.y * s
  } : {
    x: v.x * s.x,
    y: v.y * s.y
  };
}
function toUnitVector(v) {
  return scaleVector(v, 1 / vectorLength(v));
}
function rotateVector(v, t) {
  return {
    x: Math.cos(t) * v.x - Math.sin(t) * v.y,
    y: Math.sin(t) * v.x + Math.cos(t) * v.y
  };
}
function addVectors(v1, v2) {
  return {
    x: v1.x + v2.x,
    y: v1.y + v2.y
  };
}
function subtractVectors(v1, v2) {
  return {
    x: v1.x - v2.x,
    y: v1.y - v2.y
  };
}
function dotVectors(v1, v2) {
  return v1.x * v2.x + v1.y * v2.y;
}
function projectVector(v1, v2) {
  const unitV2 = toUnitVector(v2);
  return scaleVector(unitV2, dotVectors(v1, unitV2));
}
function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}
function toExactTransform({
  x,
  y,
  w,
  h,
  t,
  p
}, scale) {
  return {
    x: x * scale.x,
    y: y * scale.y,
    w: w * scale.x,
    h: h * scale.y,
    t,
    p
  };
}
function toRelativeTransform({
  x,
  y,
  w,
  h,
  t,
  p
}, scale) {
  return {
    x: x / scale.x,
    y: y / scale.y,
    w: w / scale.x,
    h: h / scale.y,
    t,
    p
  };
}
function calcZIndex({
  w,
  h
}) {
  return 31 - Math.floor(Math.min(1, w * 16 * h * 9 / 144) * 30);
}

// Translate the transform in 2 directions.
// d Vector is in relative coordinates (0 to 1) to the video.
function move(transform, d, bounds) {
  let x = transform.x + d.x;
  let y = transform.y + d.y;
  // If there are bounds, restrict movement to within them.
  if (bounds) {
    x = clamp(x, bounds.left, bounds.right);
    y = clamp(y, bounds.top, bounds.bottom);
  }
  return Object.assign({}, transform, {
    x,
    y
  });
}
// Drop in dragging support for PointerTarget components.
function useDragging({
  transform,
  bounds,
  onTransformChange
}) {
  const overlay = useOverlayDimensions();
  const interaction = useRef(null);
  return {
    // Spread these event handlers onto a PointerTarget to add dragging support.
    dragHandleProps: {
      // When dragging starts, save the initial transform and pointer.
      onDragStart(pointer) {
        interaction.current = {
          transform,
          pointer
        };
      },
      // Then on every drag, we translate the original transform
      // by the change in the pointer location from where it started.
      onDrag(currentPointer) {
        if (interaction.current) {
          const {
            transform,
            pointer
          } = interaction.current;
          onTransformChange(move(transform, toVector(pointer, currentPointer), bounds));
        }
      },
      onDragEnd() {
        interaction.current = null;
      }
    },
    // Add this event handler to the root element to add dragging keyboard support
    // ASDF keys translate 1 pixel. With SHIFT, translate 10 pixels.
    onKeyboardDrag(e) {
      const {
        width,
        height
      } = overlay;
      const nudge = e.shiftKey ? 10 : 1;
      switch (e.code) {
        case 'KeyA':
          onTransformChange(move(transform, {
            x: -nudge / width,
            y: 0
          }, bounds));
          break;
        case 'KeyW':
          onTransformChange(move(transform, {
            x: 0,
            y: -nudge / height
          }, bounds));
          break;
        case 'KeyD':
          onTransformChange(move(transform, {
            x: nudge / width,
            y: 0
          }, bounds));
          break;
        case 'KeyS':
          onTransformChange(move(transform, {
            x: 0,
            y: nudge / height
          }, bounds));
          break;
      }
    }
  };
}

const UNIT_CONFIG = {
  seconds: {
    conversion: 1,
    label: 'sec'
  },
  minutes: {
    conversion: 60,
    label: 'min'
  },
  hours: {
    conversion: 60 * 60,
    label: 'hrs'
  },
  days: {
    conversion: 60 * 60 * 24,
    label: 'days'
  }
};
const Stopwatch = ({
  id,
  config: {
    units: _units = 'seconds',
    precision: _precision = 2,
    x,
    y,
    preventReset
  },
  dispatch: toolsDispatch
}) => {
  var _video$recordedFrameR;
  const video = useCurrentVideo();
  const {
    currentFrame
  } = usePlayback();
  const [startFrame, setStartFrame] = useState(0);
  const [transform, setTransform] = useTransform({
    x,
    y
  });
  const {
    isFocused,
    focusProps
  } = useFocusIn();
  const {
    dragHandleProps,
    onKeyboardDrag
  } = useDragging({
    transform,
    onTransformChange: setTransform,
    bounds: {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    }
  });
  useEffect(() => {
    if (video != null && video.eventStartFrame) {
      setStartFrame(video.eventStartFrame);
    }
  }, [video]);
  const recordedFrameRate = (_video$recordedFrameR = video == null ? void 0 : video.recordedFrameRate) != null ? _video$recordedFrameR : 30;
  const stopwatchFrame = currentFrame - startFrame;
  const time = stopwatchFrame / recordedFrameRate / UNIT_CONFIG[_units].conversion;
  return h(PointerTarget, dragHandleProps, h("div", _extends({
    className: " p-absolute p-cursor-move p-bg-shadow p-text-white p-border-2 p-border-white p-rounded focus:p-outline-none focus:p-shadow-focus ",
    style: {
      transform: 'translate(-50%, -50%)',
      left: `${(transform.x * 100).toFixed(2)}%`,
      top: `${(transform.y * 100).toFixed(2)}%`,
      zIndex: isFocused ? 40 : 29
    },
    tabIndex: 0,
    role: "group",
    "aria-label": "Stopwatch"
  }, focusProps, {
    onKeyDown: onKeyboardDrag
  }), h("div", {
    className: "p-flex p-items-center"
  }, h("div", {
    className: "p-p-2"
  }, h("p", {
    className: "p-text-center p-whitespace-nowrap"
  }, h("span", {
    className: "p-font-mono"
  }, time.toFixed(_precision)), ' ', UNIT_CONFIG[_units].label), h("p", {
    className: "p-text-center p-whitespace-nowrap"
  }, "Frame ", h("span", {
    className: "p-font-mono"
  }, stopwatchFrame)), h("p", {
    className: "p-text-center p-whitespace-nowrap"
  }, h("span", {
    className: "p-font-mono"
  }, video == null ? void 0 : video.recordedFrameRate), ' ', "fps")), !preventReset && h(ToolButton, {
    "aria-label": "Reset Time",
    className: `
            p-rounded-full p-bg-white p-text-black p-ml-0 p-m-2
            focus:p-outline-none focus:p-shadow-focus
            active:p-bg-gray-200
          `,
    onClick: () => setStartFrame(currentFrame)
  }, h(Icon, {
    icon: "time-reset",
    className: "p-w-12 p-h-12 p-p-2"
  }))), isFocused && h(CloseButton, {
    className: `
              p-top-0 p-right-0 p--mr-10 p--mt-10
            `,
    label: "Stopwatch",
    dispatch: toolsDispatch,
    id: id
  })));
};

const _excluded$4 = ["t"];
// Rotate a transform, keeping the angle between 0 and 2 pi.
function rotate(_ref, dt) {
  let {
      t
    } = _ref,
    transform = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  return Object.assign({}, transform, {
    t: (t + dt + 4 * Math.PI) % (2 * Math.PI)
  });
}
// Drop in rotation support for PointerTarget handle.
function useRotation({
  transform,
  onTransformChange
}) {
  const {
    width,
    height
  } = useOverlayDimensions();
  const interaction = useRef(null);
  return {
    // Spread these event handlers onto a PointerTarget handle to add rotation support.
    rotateHandleProps: {
      // When dragging starts, save the initial transform
      // and angle of the vector from the center to the pointer.
      // We have to convert the transform to an exact transform since
      // The scale for width and height are different.
      onDragStart(pointer) {
        const exactTransform = toExactTransform(transform, {
          x: width,
          y: height
        });
        interaction.current = {
          transform: exactTransform,
          t: vectorDirection(toVector(exactTransform, {
            x: pointer.x * width,
            y: pointer.y * height
          }))
        };
      },
      // Then on every drag, we rotate the original transform
      // by the change in angle of the vector from the center to the pointer.
      onDrag(pointer) {
        if (interaction.current) {
          const {
            transform,
            t
          } = interaction.current;
          const newT = vectorDirection(toVector(transform, {
            x: pointer.x * width,
            y: pointer.y * height
          }));
          onTransformChange(toRelativeTransform(rotate(transform, newT - t), {
            x: width,
            y: height
          }));
        }
      },
      onDragEnd() {
        interaction.current = null;
      }
    },
    // Add this event handler to the root element to add rotation keyboard support
    // QE keys rotate 1 degree. With SHIFT, rotate 10 degrees.
    onKeyboardRotate(e) {
      const nudge = Math.PI / 180 * (e.shiftKey ? 10 : 1);
      switch (e.code) {
        case 'KeyQ':
          onTransformChange(rotate(transform, -nudge));
          break;
        case 'KeyE':
          onTransformChange(rotate(transform, nudge));
          break;
      }
    }
  };
}

const MIN_SIZE = 40;
// Resizes a transform from one of the corners.
// Limits resizing when up against a boundary.
// Supports preserving the aspect ratio.
function resize({
  x,
  y,
  w,
  h,
  t,
  p
}, ds, direction, lockAspect = false, bounds) {
  const size = {
    x: w,
    y: h
  };
  // Adjust the sizing vector to conform to the aspect ratio.
  if (lockAspect) {
    ds = projectVector(ds, size);
  }
  // Limit the sizing vector to prevent the transform from being too small.
  const newSize = addVectors(size, ds);
  if (newSize.x < MIN_SIZE || newSize.y < MIN_SIZE) {
    // If locking the aspect ratio, find the minimum size that preserves the ratio.
    if (lockAspect) {
      const aspect = size.x / size.y;
      newSize.x = MIN_SIZE;
      newSize.y = MIN_SIZE;
      if (aspect > 1) {
        newSize.y = MIN_SIZE;
        newSize.x = MIN_SIZE * aspect;
      } else {
        newSize.x = MIN_SIZE;
        newSize.y = MIN_SIZE / aspect;
      }
    }
    // Otherwise we can limit the size of each dimension independently.
    else {
      newSize.x = Math.max(newSize.x, MIN_SIZE);
      newSize.y = Math.max(newSize.y, MIN_SIZE);
    }
    // Update the change in size vector.
    ds = subtractVectors(newSize, size);
  }
  // Calculate the direction of the change in position
  // using the rotation of the tool
  // and the direction of the handle from the center.
  const positionChange = rotateVector(scaleVector(ds, scaleVector(direction, 0.5)), t);
  // Scale the resize down if the boundary is reached.
  let scale = 1;
  if (bounds) {
    scale = [
    // Scale the horizontal movement so it does not exceed the boundary.
    ((positionChange.x > 0 ? bounds.right : bounds.left) - x) / positionChange.x,
    // Scale the vertical movement so it does not exceed the boundary.
    ((positionChange.y > 0 ? bounds.bottom : bounds.top) - y) / positionChange.y]
    // Find the smaller of the two scales that is between 0 and 1.
    .reduce((min, scale) => scale >= 0 && scale < min ? scale : min, 1);
  }
  // Apply the scale to the transform.
  return {
    x: x + scale * positionChange.x,
    y: y + scale * positionChange.y,
    w: w + scale * ds.x,
    h: h + scale * ds.y,
    t,
    p
  };
}
// Drop in resize support for PointerTarget handles in the corners.
function useBoxResize({
  lockAspect,
  transform,
  bounds,
  onTransformChange
}) {
  const {
    width,
    height
  } = useOverlayDimensions();
  const interaction = useRef(null);
  // Convert bounds from relative to exact coordinates.
  const exactBounds = bounds ? {
    left: bounds.left * width,
    right: bounds.right * width,
    top: bounds.top * height,
    bottom: bounds.bottom * height
  } : bounds;
  return {
    // Spread these event handlers onto a PointerTarget handle to add resize support.
    // The anchor is location of the corner opposite the handle.
    // This point stays constant during resizing.
    resizeHandleProps(anchor) {
      return {
        // When dragging starts, save the initial transform
        // and the vector to the pointer after factoring out the tool rotation.
        // We have to convert the transform to an exact transform since
        // The scale for width and height are different.
        onDragStart(pointer) {
          const exactTransform = toExactTransform(transform, {
            x: width,
            y: height
          });
          interaction.current = {
            transform: exactTransform,
            pointer: rotateVector(toVector(exactTransform, {
              x: pointer.x * width,
              y: pointer.y * height
            }), -exactTransform.t)
          };
        },
        // Then on every drag, we resize the original transform
        // by the change in size of the vectors component parts
        // after factoring out the rotation.
        onDrag(currentPointer) {
          if (interaction.current) {
            const {
              transform,
              pointer
            } = interaction.current;
            const endPointer = rotateVector(toVector(transform, {
              x: currentPointer.x * width,
              y: currentPointer.y * height
            }), -transform.t);
            onTransformChange(toRelativeTransform(resize(transform,
            // Get the change in vector dimensions in the direction of the handle.
            scaleVector(subtractVectors(endPointer, pointer), anchorDirection[anchor]), anchorDirection[anchor], lockAspect, exactBounds), {
              x: width,
              y: height
            }));
          }
        },
        onDragEnd() {
          interaction.current = null;
        }
      };
    },
    // Add this event handler to the root element to add resize keyboard support
    // Resizing with the keyboard keeps the tool in the center.
    // FR keys resize vertically 1 pixel. With SHIFT, resize 10 pixels.
    // ZX keys resize horizontally 1 pixel. With SHIFT, resize 10 pixels.
    onKeyboardResize(e) {
      const nudge = e.shiftKey ? 10 : 1;
      function nudgeResize(x, y) {
        onTransformChange(toRelativeTransform(resize(toExactTransform(transform, {
          x: width,
          y: height
        }), {
          x,
          y
        }, anchorDirection[ResizeAnchor.Center], lockAspect, exactBounds), {
          x: width,
          y: height
        }));
      }
      switch (e.code) {
        case 'KeyF':
          nudgeResize(0, -nudge);
          break;
        case 'KeyR':
          nudgeResize(0, nudge);
          break;
        case 'KeyZ':
          nudgeResize(-nudge, 0);
          break;
        case 'KeyX':
          nudgeResize(nudge, 0);
          break;
      }
    }
  };
}

// How much touches have to move before registering a gesture.
const DISTANCE_THRESHOLD = 0.2;
// The breakpoint for dot product of gesture motion to be rotating vs resizing.
const GESTURE_THRESHOLD = 0.5;
// Drop in rotation and resize gesture support for PointerTarget.
function useGestures({
  transform,
  onTransformChange,
  resizable,
  rotatable
}) {
  const {
    width,
    height
  } = useOverlayDimensions();
  const interaction = useRef(null);
  return {
    // Spread these event handlers onto a PointerTarget to add gesture support.
    gestureHandleProps: {
      // When the gesture starts, save the exact transform
      // and the touches.
      // We can't determine which gesture is being performed yet.
      onGestureStart(e) {
        if (e.length === 2) {
          // Touches should be in increasing order by id.
          e = e[0].id > e[1].id ? [e[1], e[0]] : e;
          interaction.current = {
            transform: toExactTransform(transform, {
              x: width,
              y: height
            }),
            touches: [{
              x: e[0].x * width,
              y: e[0].y * height
            }, {
              x: e[1].x * width,
              y: e[1].y * height
            }]
          };
        } else {
          interaction.current = null;
        }
      },
      // During the gesture, we identify which gesture is being performed
      // after the touches have moved beyond the threshold.
      // Then for rotation gestures we change the angle based on the change in angle of the vector between points.
      // For resize gestures we change the size based on the change in distance between the points.
      onGesture(e) {
        if (interaction.current && e.length == 2) {
          const {
            transform,
            touches
          } = interaction.current;
          let {
            gestureType
          } = interaction.current;
          // Reorder the touches in increasing order by id.
          e = e[0].id > e[1].id ? [e[1], e[0]] : e;
          const newTouches = [{
            x: e[0].x * width,
            y: e[0].y * height
          }, {
            x: e[1].x * width,
            y: e[1].y * height
          }];
          // Gesture has not been identified yet.
          // We need to wait until points have moved enough to identify the gesture.
          if (!gestureType && distance(touches[0], newTouches[0]) + distance(touches[1], newTouches[1]) > DISTANCE_THRESHOLD) {
            // Dot the touch move direction with the base line vector.
            // This determines whether touches are moving along the line between them
            // or if they are being rotated around the center.
            const baseVector = toUnitVector(toVector(touches[0], touches[1]));
            const dot1 = Math.abs(dotVectors(baseVector, toUnitVector(toVector(touches[0], newTouches[0]))));
            const dot2 = Math.abs(dotVectors(baseVector, toUnitVector(toVector(touches[1], newTouches[1]))));
            // The closer to 0, the more the touch moved off of the base line.
            if (dot1 < GESTURE_THRESHOLD || dot2 < GESTURE_THRESHOLD) {
              gestureType = 'rotate';
              interaction.current.gestureType = gestureType;
            } else {
              gestureType = 'resize';
              interaction.current.gestureType = gestureType;
            }
          }
          // If we have identified gestures we can transform the tool.
          if (gestureType === 'rotate' && rotatable) {
            // Calculate the change in angle between start and end touches.
            const baseAngle = vectorDirection(toVector(touches[0], touches[1]));
            const newAngle = vectorDirection(toVector(newTouches[0], newTouches[1]));
            onTransformChange(toRelativeTransform(rotate(transform, newAngle - baseAngle), {
              x: width,
              y: height
            }));
          } else if (gestureType === 'resize' && resizable) {
            // Calculate the ratio of distances between start and end touches.
            const scale = distance(newTouches[0], newTouches[1]) / distance(touches[0], touches[1]);
            onTransformChange(toRelativeTransform(Object.assign({}, transform, {
              w: transform.w * scale,
              h: transform.h * scale
            }), {
              x: width,
              y: height
            }));
          }
        } else {
          interaction.current = null;
        }
      },
      onGestureEnd() {
        interaction.current = null;
      }
    }
  };
}

const _excluded$3 = ["position", "type", "t"];
const ToolHandle = _ref => {
  let {
      position,
      type,
      t = 0
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  let boundsClassName = '';
  let handleClassName = '';
  if (type === 'rotate') {
    boundsClassName = 'p-cursor-rotate';
    switch (position) {
      case 't':
        boundsClassName = 'p-cursor-rotate p-left-1/2 p-top-0 p--ml-6 p--mt-8';
        handleClassName = 'p-mt-3';
        break;
      case 'r':
        boundsClassName = 'p-cursor-rotate p-right-0 p-top-1/2 p--mr-8 p--mt-6';
        handleClassName = 'p-mr-3';
        break;
      case 'b':
        boundsClassName = 'p-cursor-rotate p-left-1/2 p-bottom-0 p--ml-6 p--mb-8';
        handleClassName = 'p-mb-3';
        break;
      case 'l':
        boundsClassName = 'p-cursor-rotate p-left-0 p-top-1/2 p--ml-8 p--mt-6';
        handleClassName = 'p-ml-3';
        break;
      default:
        throw new Error(`${type} handle in position ${position} not supported.`);
    }
  } else {
    const sector = Math.round(12 * t / Math.PI) % 12;
    const sectorReflected = (sector + 6) % 12;
    switch (position) {
      case 'tr':
        boundsClassName = `p-cursor-resize-${sector} p-right-0 p-top-0 p--mt-8 p--mr-8`;
        handleClassName = 'p-mt-3 p-mr-3';
        break;
      case 'tl':
        boundsClassName = `p-cursor-resize-${sectorReflected} p-left-0 p-top-0 p--mt-8 p--ml-8`;
        handleClassName = 'p-mt-3 p-ml-3';
        break;
      case 'bl':
        boundsClassName = `p-cursor-resize-${sector} p-bottom-0 p-left-0 p--mb-8 p--ml-8`;
        handleClassName = 'p-mb-3 p-ml-3';
        break;
      case 'br':
        boundsClassName = `p-cursor-resize-${sectorReflected} p-right-0 p-bottom-0 p--mb-8 p--mr-8`;
        handleClassName = 'p-mb-3 p-mr-3';
        break;
      default:
        throw new Error(`${type} handle in position ${position} not supported.`);
    }
  }
  return h(PointerTarget, _extends({}, props, {
    "data-testid": `${type}-handle`
  }), h("div", {
    className: `
          p-absolute p-w-12 p-h-12 p-flex p-items-center p-justify-center
          ${boundsClassName}
        `
  }, h("div", {
    className: `
            ${type === 'resize' ? 'p-rounded-sm' : 'p-rounded-full'}
            p-border p-border-black p-bg-white p-w-2 p-h-2
            ${handleClassName}
          `
  })));
};

const BoxTool = ({
  label,
  className,
  transform,
  bounds,
  dispatch,
  id,
  onTransformChange,
  draggable: _draggable = false,
  rotatable: _rotatable = false,
  resizable: _resizable = false,
  lockAspect: _lockAspect = false,
  closeOffset,
  children
}) => {
  const {
    focusProps,
    isFocused
  } = useFocusIn();
  const {
    dragHandleProps,
    onKeyboardDrag
  } = useDragging({
    transform,
    bounds: bounds ? bounds : {
      top: -0.05,
      left: -0.05,
      bottom: 1.0,
      right: 1.1
    },
    onTransformChange
  });
  const {
    rotateHandleProps,
    onKeyboardRotate
  } = useRotation({
    transform,
    onTransformChange
  });
  const {
    resizeHandleProps,
    onKeyboardResize
  } = useBoxResize({
    lockAspect: _lockAspect,
    transform,
    bounds,
    onTransformChange
  });
  const {
    gestureHandleProps
  } = useGestures({
    transform,
    onTransformChange,
    resizable: _resizable,
    rotatable: _rotatable
  });
  return h(PointerTarget, _extends({}, _draggable && dragHandleProps, (_rotatable || _resizable) && gestureHandleProps, {
    "data-testid": "drag-target"
  }), h("div", _extends({}, focusProps, {
    style: {
      transform: `
            translate(-50%, -50%)
            rotate(${transform.t.toFixed(3)}rad)
          `,
      left: `${(transform.x * 100).toFixed(2)}%`,
      top: `${(transform.y * 100).toFixed(2)}%`,
      width: `${(transform.w * 100).toFixed(2)}%`,
      height: `${(transform.h * 100).toFixed(2)}%`,
      zIndex: isFocused ? 40 : calcZIndex(transform)
    },
    className: `
          p-absolute focus:p-outline-none focus:p-shadow-focus
          ${_draggable ? 'p-cursor-move' : ''}
          ${className || ''}
        `,
    tabIndex: 0,
    role: "group",
    "aria-label": label,
    onKeyDown: e => {
      _draggable && onKeyboardDrag(e);
      _resizable && onKeyboardResize(e);
      _rotatable && onKeyboardRotate(e);
    }
  }), children, isFocused && h(Fragment, null, h(CloseButton, {
    className: "p-top-0 p-right-0",
    offset: closeOffset,
    dispatch: dispatch,
    id: id,
    label: label
  }), _rotatable && h(Fragment, null, h(ToolHandle, _extends({}, rotateHandleProps, {
    type: "rotate",
    position: "t"
  })), h(ToolHandle, _extends({}, rotateHandleProps, {
    type: "rotate",
    position: "r"
  })), h(ToolHandle, _extends({}, rotateHandleProps, {
    type: "rotate",
    position: "b"
  })), h(ToolHandle, _extends({}, rotateHandleProps, {
    type: "rotate",
    position: "l"
  }))), _resizable && h(Fragment, null, h(ToolHandle, _extends({
    type: "resize",
    position: "tr",
    t: transform.t
  }, resizeHandleProps(ResizeAnchor.BottomLeft))), h(ToolHandle, _extends({
    type: "resize",
    position: "tl",
    t: transform.t
  }, resizeHandleProps(ResizeAnchor.BottomRight))), h(ToolHandle, _extends({
    type: "resize",
    position: "br",
    t: transform.t
  }, resizeHandleProps(ResizeAnchor.TopLeft))), h(ToolHandle, _extends({
    type: "resize",
    position: "bl",
    t: transform.t
  }, resizeHandleProps(ResizeAnchor.TopRight)))))));
};

const Ruler = ({
  config: {
    x,
    y,
    w,
    h: ht,
    t,
    rotatable,
    resizable,
    draggable
  },
  dispatch,
  id
}) => {
  const {
    spacing,
    majorIncrement,
    units,
    color
  } = useCalibration();
  const overlay = useOverlayDimensions();
  const [transform, setTransform] = useTransform({
    x,
    y,
    w,
    h: ht,
    t
  });
  const marks = useMemo(() => {
    const places = -1 * Math.min(0, parseInt(majorIncrement.toExponential().split('e')[1]));
    const width = overlay.width * transform.w;
    let i = 0;
    const marks = [];
    const microSpacing5 = spacing / 5;
    const microSpacing2 = spacing / 2;
    for (let x = 0; x < width; x += spacing) {
      marks.push(h("line", {
        "data-testid": "ruler-mark",
        key: i,
        x1: x,
        y1: "0%",
        x2: x,
        y2: i % 10 === 0 ? '100%' : i % 5 === 0 ? '75%' : '50%',
        strokeWidth: i % 5 === 0 ? 2 : 1,
        strokeLinecap: "square"
      }));
      if (i % 10 === 0) {
        marks.push(h("text", {
          x: x,
          y: "100%",
          textAnchor: "middle",
          fontSize: "14",
          transform: "translate(0, 18)",
          "data-testid": "ruler-label",
          "aria-hidden": true
        }, i === 0 ? 0 : (majorIncrement * (i / 10)).toFixed(places)));
      }
      if (i == 0 && units) {
        marks.push(h("text", {
          x: "7",
          y: "100%",
          fontSize: "14",
          transform: "translate(0, 18)",
          "data-testid": "ruler-units",
          "aria-hidden": true
        }, units));
      }
      i++;
      if (microSpacing5 > 4) {
        for (let j = 1; j < 5 && x + j * microSpacing5 < width; j++) {
          const newX = x + j * microSpacing5;
          marks.push(h("line", {
            "data-testid": "ruler-mark-micro",
            key: `${i}-${j}`,
            x1: newX,
            y1: "0%",
            x2: newX,
            y2: "25%",
            strokeWidth: 1,
            strokeLinecap: "square"
          }));
        }
      } else if (microSpacing2 > 4 && spacing > 12) {
        const newX = x + microSpacing2;
        marks.push(h("line", {
          "data-testid": "ruler-mark-micro",
          key: `${i}-half`,
          x1: newX,
          y1: "0%",
          x2: newX,
          y2: "25%",
          strokeWidth: 1,
          strokeLinecap: "square"
        }));
      }
    }
    return marks;
  }, [spacing, overlay.width, transform.w, majorIncrement, units]);
  return h(BoxTool, {
    dispatch: dispatch,
    id: id,
    transform: transform,
    onTransformChange: setTransform,
    draggable: draggable,
    resizable: resizable,
    rotatable: rotatable,
    label: "Ruler",
    className: "p-text-black"
  }, h("svg", {
    width: "100%",
    height: "100%",
    className: `
          p-stroke-current p-fill-current p-overflow-visible
          ${color === 'green' ? 'p-text-green-500' : ''}
          ${color === 'red' ? 'p-text-red-600' : ''}
          ${color === 'black' ? 'p-text-black' : ''}
          ${color === 'white' ? 'p-text-white' : ''}
        `
  }, marks));
};

const Grid = ({
  config: {
    x,
    y,
    w,
    h: ht,
    t,
    bounds,
    resizable,
    rotatable,
    draggable
  },
  id,
  dispatch
}) => {
  const {
    spacing,
    majorIncrement,
    units,
    color
  } = useCalibration();
  const overlay = useOverlayDimensions();
  const [transform, setTransform] = useTransform({
    x,
    y,
    w,
    h: ht,
    t
  });
  const marks = useMemo(() => {
    const places = -1 * Math.min(0, parseInt(majorIncrement.toExponential().split('e')[1]));
    const width = overlay.width * transform.w;
    const height = overlay.height * transform.h;
    let i = 0;
    const marks = [];
    for (let x = 0; x < width; x += spacing) {
      marks.push(h("line", {
        "data-testid": "ruler-mark",
        x1: x,
        y1: "0%",
        x2: x,
        y2: "100%",
        strokeWidth: i % 5 === 0 ? 2 : 1,
        strokeLinecap: "square"
      }));
      if (i % 10 === 0) {
        marks.push(h("text", {
          x: x,
          y: "100%",
          textAnchor: "middle",
          fontSize: "14",
          transform: "translate(0, 18)",
          "data-testid": "ruler-label",
          "aria-hidden": true
        }, i === 0 ? 0 : (majorIncrement * (i / 10)).toFixed(places)));
      }
      if (i == 0 && units) {
        marks.push(h("text", {
          x: "7",
          y: "100%",
          fontSize: "18",
          transform: "translate(0, 18)",
          strokeLinecap: "square",
          "data-testid": "ruler-units",
          "aria-hidden": true
        }, units));
      }
      i++;
    }
    i = 0;
    for (let y = 0; y < height; y += spacing) {
      marks.push(h("line", {
        "data-testid": "ruler-mark",
        x1: "0%",
        y1: height - y,
        x2: "100%",
        y2: height - y,
        strokeWidth: i % 5 === 0 ? 2 : 1
      }));
      if (i % 10 === 0) {
        marks.push(h("text", {
          x: "-8",
          y: height - y,
          textAnchor: "middle",
          fontSize: "14",
          transform: `rotate(-90 -8 ${height - y})`,
          "data-testid": "ruler-label",
          "aria-hidden": true
        }, i === 0 ? 0 : (majorIncrement * (i / 10)).toFixed(places)));
      }
      i++;
    }
    return marks;
  }, [spacing, overlay.width, overlay.height, transform.w, transform.h, majorIncrement, units]);
  return h(BoxTool, {
    dispatch: dispatch,
    id: id,
    transform: transform,
    bounds: bounds,
    onTransformChange: setTransform,
    draggable: draggable,
    rotatable: rotatable,
    resizable: resizable,
    label: "Grid",
    className: "p-text-black"
  }, h("svg", {
    width: "100%",
    height: "100%",
    className: `
          p-stroke-current p-fill-current p-overflow-visible
          ${color === 'green' ? 'p-text-green-500' : ''}
          ${color === 'red' ? 'p-text-red-600' : ''}
          ${color === 'black' ? 'p-text-black' : ''}
          ${color === 'white' ? 'p-text-white' : ''}
        `
  }, marks));
};

const ImageTool = ({
  config: {
    src,
    w,
    h: ht,
    x,
    y,
    t,
    bounds,
    rotatable,
    resizable,
    draggable,
    label,
    lockAspect
  },
  id,
  dispatch
}) => {
  const [isLoading, setLoading] = useState(true);
  const [transform, setTransform] = useTransform({
    x,
    y,
    w,
    h: ht,
    t
  });
  return h(BoxTool, {
    dispatch: dispatch,
    id: id,
    transform: transform,
    bounds: bounds,
    onTransformChange: setTransform,
    draggable: draggable,
    rotatable: rotatable,
    resizable: resizable,
    label: label,
    lockAspect: lockAspect
  }, isLoading && h(Loading, {
    className: "p-absolute p-inset-0",
    size: "sm"
  }), h("img", {
    className: "p-w-full p-h-full",
    src: src,
    onLoad: () => setLoading(false),
    draggable: false
  }));
};

const Calibration = () => {
  const {
    left,
    right,
    color,
    setLeftPoint,
    setRightPoint
  } = useCalibration();
  const {
    width,
    height
  } = useOverlayDimensions();
  const {
    dragHandleProps: leftDragProps,
    onKeyboardDrag: onLeftKeyboardDrag
  } = useDragging({
    transform: Object.assign({}, left, {
      w: 0,
      h: 0,
      p: 0,
      t: 0
    }),
    bounds: {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    },
    onTransformChange: ({
      x,
      y
    }) => setLeftPoint({
      x,
      y
    })
  });
  const {
    dragHandleProps: rightDragProps,
    onKeyboardDrag: onRightKeyboardDrag
  } = useDragging({
    transform: Object.assign({}, right, {
      w: 0,
      h: 0,
      p: 0,
      t: 0
    }),
    bounds: {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    },
    onTransformChange: ({
      x,
      y
    }) => setRightPoint({
      x,
      y
    })
  });
  // Compute pixel points.
  const rightX = right.x * width;
  const rightY = right.y * height;
  const leftX = left.x * width;
  const leftY = left.y * height;
  // Get angle of calibration line.
  let t = Math.atan2(rightY - leftY, rightX - leftX);
  // How much to move the line in from its ends.
  const dx = 3 * Math.cos(t);
  const dy = 3 * Math.sin(t);
  // Convert to degrees.
  t = t / Math.PI * 180;
  return h("div", {
    className: "p-w-full p-h-full p-relative",
    "data-testid": "calibration"
  }, h("svg", {
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    className: `
          ${color === 'green' ? 'p-text-green-500' : ''}
          ${color === 'red' ? 'p-text-red-600' : ''}
          ${color === 'black' ? 'p-text-black' : ''}
          ${color === 'white' ? 'p-text-white' : ''}
        `
  }, h("g", {
    className: "p-stroke-current",
    opacity: "0.8",
    strokeWidth: 4
  }, h("line", {
    x1: leftX + dx,
    y1: leftY + dy,
    x2: rightX - dx,
    y2: rightY - dy,
    "data-testid": "calibration-line"
  }), h(PointerTarget, _extends({}, leftDragProps, {
    "data-testid": "calibration-left"
  }), h("g", {
    "pointer-events": "all",
    transform: `translate(${leftX} ${leftY}) rotate(${t}) translate(-22 -22)`,
    tabindex: "0",
    className: `
                focus:p-outline-none
                ${color === 'green' ? 'focus:p-text-green-700' : ''}
                ${color === 'red' ? 'focus:p-text-red-800' : ''}
                ${color === 'black' ? 'focus:p-text-gray-700' : ''}
                ${color === 'white' ? 'focus:p-text-gray-500' : ''}
              `,
    onKeyDown: onLeftKeyboardDrag
  }, h("rect", {
    width: "44",
    height: "44",
    fill: "transparent",
    "stroke-width": "0"
  }), h("path", {
    d: "M 36 11 L 25 22 L 36 33",
    fill: "transparent"
  }))), h(PointerTarget, _extends({}, rightDragProps, {
    "data-testid": "calibration-right"
  }), h("g", {
    "pointer-events": "all",
    transform: `translate(${rightX} ${rightY}) rotate(${t}) translate(-22 -22)`,
    tabindex: "0",
    className: `
                focus:p-outline-none
                ${color === 'green' ? 'focus:p-text-green-800' : ''}
                ${color === 'red' ? 'focus:p-text-red-900' : ''}
                ${color === 'black' ? 'focus:p-text-gray-700' : ''}
                ${color === 'white' ? 'focus:p-text-gray-500' : ''}
              `,
    onKeyDown: onRightKeyboardDrag
  }, h("rect", {
    width: "44",
    height: "44",
    fill: "transparent",
    "stroke-width": "0"
  }), h("path", {
    d: "M 8 11 L 19 22 L 8 33",
    fill: "transparent"
  }))))));
};

const MIN_RAD = 120;
// Drop in resize support for PointerTarget handles on circular tools.
function useCircleResize({
  transform,
  onTransformChange
}) {
  const {
    width,
    height
  } = useOverlayDimensions();
  const interaction = useRef(null);
  return {
    // Spread these event handlers onto a PointerTarget handle to add resize support.
    resizeHandleProps: {
      // When dragging starts, save the initial transform
      // And the distance to the pointer from the center.
      // We have to convert the transform to an exact transform since
      // the scale for width and height are different.
      onDragStart(pointer) {
        const exactTransform = toExactTransform(transform, {
          x: width,
          y: height
        });
        interaction.current = {
          transform: exactTransform,
          radius: vectorLength(toVector(exactTransform, {
            x: pointer.x * width,
            y: pointer.y * height
          }))
        };
      },
      // Then on every drag, we resize the original transform
      // by the change in distance from the center to the pointer.
      onDrag(currentPointer) {
        if (interaction.current) {
          const {
            transform,
            radius
          } = interaction.current;
          const newRadius = Math.max(MIN_RAD, vectorLength(toVector(transform, {
            x: currentPointer.x * width,
            y: currentPointer.y * height
          })));
          const ds = 2 * (newRadius - radius);
          onTransformChange(toRelativeTransform(Object.assign({}, transform, {
            w: transform.w + ds,
            h: transform.h + ds
          }), {
            x: width,
            y: height
          }));
        }
      },
      onDragEnd() {
        interaction.current = null;
      }
    }
  };
}

const TO_RAD = Math.PI / 180;
const SWEEP_MIN_THRESHOLD = 90 * TO_RAD;
const SWEEP_MAX_THRESHOLD = 270 * TO_RAD;
const MIN_SWEEP = 30 * TO_RAD;
// Drop in protractor sector resize support for PointerTarget handle.
function useSectorResize({
  transform,
  onTransformChange
}) {
  const {
    width,
    height
  } = useOverlayDimensions();
  const interaction = useRef(null);
  return {
    // Spread these event handlers onto a PointerTarget handle to add sector resize support.
    sectorResizeHandleProps: {
      onDragStart() {
        // When dragging starts, save the initial transform
        // And the direction of the sector sweep.
        interaction.current = {
          transform,
          sweep: transform.p > 0 ? 1 : -1
        };
      },
      // Then on every drag, we increase/decrease the sector angle
      // to match the angle of the vector from the center to the pointer.
      // We also have to take into consideration whether the sector angle is crossing the initial edge
      // or if it is at its maximum angle.
      onDrag(pointer) {
        if (interaction.current) {
          const {
            transform
          } = interaction.current;
          let p = vectorDirection(rotateVector(toVector({
            x: transform.x * width,
            y: transform.y * height
          }, {
            x: pointer.x * width,
            y: pointer.y * height
          }), -transform.t));
          // Vector direction returns -180 to 180, but p is -360 to 360
          // so we have to convert.
          if (
          // Sweep is greater than 180 degrees.
          transform.p > SWEEP_MIN_THRESHOLD && p < 0 ||
          // Sweep is crossing the initial edge.
          p < -SWEEP_MIN_THRESHOLD && transform.p > 0) {
            p += 2 * Math.PI;
          } else if (
          // Sweep is less than -180 degrees.
          transform.p < -SWEEP_MIN_THRESHOLD && p > 0 ||
          // Sweep is crossing the initial edge.
          p > SWEEP_MIN_THRESHOLD && transform.p < 0) {
            p -= 2 * Math.PI;
          }
          // Sweep is maxed out in either direction.
          if (transform.p > SWEEP_MAX_THRESHOLD && p < SWEEP_MAX_THRESHOLD - 0.2) {
            p = 2 * Math.PI;
          } else if (transform.p < -SWEEP_MAX_THRESHOLD && p > -SWEEP_MAX_THRESHOLD + 0.2) {
            p = -2 * Math.PI;
          }
          // Min sweep angle.
          p = Math.abs(p) < MIN_SWEEP ? Math.sign(p) * MIN_SWEEP : p;
          const newTransform = Object.assign({}, transform, {
            p
          });
          onTransformChange(newTransform);
          interaction.current.transform = newTransform;
        }
      },
      onDragEnd() {
        interaction.current = null;
      }
    }
  };
}

function protractorMark(i) {
  return h(Fragment, null, h("line", {
    key: i,
    strokeWidth: i % 5 === 0 ? 2 : 1,
    x1: i % 15 === 0 ? '40%' : i % 5 === 0 ? '42.5%' : '45%',
    y1: "0",
    x2: "50%",
    y2: "0",
    transform: `rotate(${i})`,
    "data-testid": "protractor-mark"
  }), i % 30 === 0 && h("g", {
    transform: `rotate(${i}) translate(8 0)`,
    "data-testid": "protractor-label"
  }, h("svg", {
    x: "50%",
    y: "0",
    className: "p-overflow-visible"
  }, h("text", {
    textAnchor: "middle",
    transform: `rotate(90)`,
    className: "p-fill-current",
    strokeWidth: 1,
    "aria-hidden": true
  }, Math.abs(i)))));
}
function handle(x, y, cx, cy, r, t) {
  return h("g", {
    transform: `rotate(${t / Math.PI * 180})`,
    "pointer-events": "all"
  }, h("svg", {
    transform: "translate(1 0)",
    x: x,
    y: y,
    width: "44",
    height: "44",
    className: "p-overflow-visible"
  }, h("rect", {
    x: -22 + cx,
    y: -22 + cy,
    width: "44",
    height: "44",
    fill: "none",
    stroke: "none"
  }), h("rect", {
    x: -4 + cx,
    y: -4 + cy,
    width: "8",
    height: "8",
    fill: "white",
    stroke: "black",
    strokeWidth: "1",
    rx: r,
    ry: r
  })));
}
function sector(p, r) {
  return h("path", {
    d: `
        M 0,0
        H ${r}
        A ${r} ${r} 0 
          ${Math.abs(p) < Math.PI ? 0 : 1}
          ${p > 0 ? 1 : 0}
          ${r * Math.cos(p)}
          ${r * Math.sin(p)}
        L 0 0
        Z
      `,
    fill: "none"
  });
}
const Protractor = ({
  config,
  dispatch,
  id
}) => {
  const el = useRef(null);
  const {
    color
  } = useCalibration();
  const {
    width
  } = useOverlayDimensions();
  const {
    x,
    y,
    r,
    t,
    p,
    draggable,
    rotatable,
    resizable,
    bounds
  } = config;
  const {
    isFocused,
    focusProps
  } = useFocusIn();
  const [transform, setTransform] = useTransform({
    x,
    y,
    w: 2 * r,
    h: 2 * r * 16 / 9,
    t,
    p
  });
  const {
    dragHandleProps
  } = useDragging({
    transform,
    onTransformChange: setTransform,
    bounds: bounds ? bounds : {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    }
  });
  const {
    rotateHandleProps
  } = useRotation({
    transform,
    onTransformChange: setTransform
  });
  const {
    resizeHandleProps
  } = useCircleResize({
    transform,
    onTransformChange: setTransform
  });
  const {
    sectorResizeHandleProps
  } = useSectorResize({
    transform,
    onTransformChange: setTransform
  });
  const {
    gestureHandleProps
  } = useGestures({
    transform,
    onTransformChange: setTransform,
    rotatable,
    resizable
  });
  const radius = transform.w / 2 * width;
  const markCount = Math.min(360, 1 + Math.floor(Math.abs(transform.p / Math.PI * 180)));
  const sweep = transform.p > 0 ? 1 : -1;
  return h(PointerTarget, _extends({}, draggable && dragHandleProps, (resizable || rotatable) && gestureHandleProps, {
    onTouch: () => el.current.focus()
  }), h("div", _extends({
    className: "p-absolute p-pointer-events-none focus:p-outline-none",
    style: {
      transform: `
          translate(-50%, -50%)
          rotate(${transform.t.toFixed(3)}rad)
        `,
      left: `${(transform.x * 100).toFixed(2)}%`,
      top: `${(transform.y * 100).toFixed(2)}%`,
      width: `${(transform.w * 100).toFixed(2)}%`,
      height: `${(transform.h * 100).toFixed(2)}%`,
      zIndex: isFocused ? 40 : calcZIndex(transform)
    },
    tabIndex: 0
  }, focusProps), isFocused && h(CloseButton, {
    className: `
              absolute p-right-0 p-top-1/2 p--mr-16 p-pointer-events-auto
              ${sweep < 0 ? 'p--mt-12' : ''}
            `,
    dispatch: dispatch,
    label: "Protractor",
    id: id
  }), h("svg", {
    ref: el,
    width: "100%",
    height: "100%",
    className: `
            p-stroke-current p-fill-current p-overflow-visible focus:p-outline-none
            ${color === 'green' ? 'p-text-green-500' : ''}
            ${color === 'red' ? 'p-text-red-600' : ''}
            ${color === 'black' ? 'p-text-black' : ''}
            ${color === 'white' ? 'p-text-white' : ''}
          `,
    viewBox: `${-radius} ${-radius} ${radius * 2} ${radius * 2}`,
    fill: "transparent",
    strokeWidth: "0"
  }, h("g", {
    "pointer-events": "all"
  }, sector(transform.p, radius)), h("g", {
    strokeWidth: "2"
  }, h("line", {
    x1: "-10",
    y1: "0",
    x2: "10",
    y2: "0"
  }), h("line", {
    x1: "0",
    y1: "-10",
    x2: "0",
    y2: "10"
  })), h("g", null, Array.from({
    length: markCount
  }, (_, i) => (radius > 250 || i % 5 === 0) && protractorMark(sweep * i))), isFocused && h(Fragment, null, h("g", {
    className: "p-stroke-current p-text-focus",
    strokeWidth: "4"
  }, sector(transform.p, radius + 2)), resizable && h(Fragment, null, h(PointerTarget, sectorResizeHandleProps, handle('40%', '0', 0, 0, 2, transform.p)), h(PointerTarget, resizeHandleProps, handle('50%', '0', 2, 0, 2, transform.p / 2))), rotatable && h(PointerTarget, rotateHandleProps, handle('50%', '0', 2, 0, 8, 0))))));
};

const WHITESPACE = [0x0a, 0x20 // space
];
// Reads an ASCII decmial starting at position n and ending at the next whitespace.
// Returns the new read position, and the number.
function readASCII(data, n) {
  let char = data[n++];
  let str = '';
  do {
    str += String.fromCharCode(char);
    char = data[n++];
  } while (!WHITESPACE.includes(char));
  return {
    pos: n,
    value: parseInt(str)
  };
}
class FfmpegWorker {
  constructor() {
    this.queue = [];
    this.worker = new Worker(new URL('ffmpeg/ffmpeg-worker-webm.js', `${document.location.protocol}${document.location.host}`));
    this.worker.onmessage = this.onMessage.bind(this);
  }
  onMessage(e) {
    const msg = e.data;
    switch (msg.type) {
      // case 'stderr': {
      //   console.log(msg.data)
      //   break
      // }
      case 'done':
        {
          var _msg$data$MEMFS$;
          const imgData = (_msg$data$MEMFS$ = msg.data.MEMFS[0]) == null ? void 0 : _msg$data$MEMFS$.data;
          const promise = this.queue[0];
          if (promise) {
            this.queue.splice(0, 1);
            if (imgData) {
              try {
                const {
                  pos: heightPos,
                  value: width
                } = readASCII(imgData, 3);
                const {
                  pos: scalePos,
                  value: height
                } = readASCII(imgData, heightPos);
                const data = imgData.slice(scalePos + 4, 3 * width * height);
                promise.resolve({
                  data,
                  width,
                  height
                });
              } catch (error) {
                promise.reject(new Error('error occurred in extracting frame'));
              }
            } else {
              promise.reject(new Error('error occurred in extracting frame'));
            }
          }
          break;
        }
    }
  }
  async renderFrame(data, time) {
    const promise = new Promise((resolve, reject) => {
      this.queue.push({
        resolve,
        reject
      });
    });
    this.worker.postMessage({
      type: 'run',
      MEMFS: [{
        name: 'vid.webm',
        data
      }],
      arguments: ['-ss', time.toString(), '-i', 'vid.webm', '-frames:v', '1', 'frame.ppm']
    });
    return promise;
  }
  terminate() {
    this.worker.terminate();
  }
}

function useFrameData() {
  const video = useCurrentVideo();
  const {
    currentFrame,
    isPlaying
  } = usePlayback();
  const lastFrame = useRef();
  const renderingFrame = useRef();
  const worker = useMemo(() => new FfmpegWorker(), []);
  useEffect(() => {
    return () => worker.terminate();
  }, [worker]);
  const [status, setStatus] = useState('downloading');
  const [videoData, setVideoData] = useState(undefined);
  useEffect(() => {
    setStatus('downloading');
    setVideoData(undefined);
    setFrameData(undefined);
    lastFrame.current = undefined;
    let isTorndown = false;
    if (video != null && video.src && video.src.endsWith('.webm')) {
      fetch(video == null ? void 0 : video.src).then(response => response.arrayBuffer()).then(data => {
        if (!isTorndown) {
          setVideoData(data);
          setStatus('ready');
        }
      }).catch(error => {
        console.log(error);
        setStatus('error');
      });
    } else {
      setStatus('error');
    }
    return () => {
      isTorndown = true;
    };
  }, [video == null ? void 0 : video.src]);
  const [frameData, setFrameData] = useState(undefined);
  useEffect(() => {
    if (isPlaying) {
      setStatus('playing');
    } else if (status === 'playing') {
      setStatus('ready');
    } else if (lastFrame.current !== currentFrame) {
      renderingFrame.current = currentFrame;
      if ((status === 'ready' || status === 'rendered' || status === 'error') && videoData) {
        const time = video != null && video.playbackFrameRate ?
        // Without this negative offset, ffmpeg renders the next frame instead of the current one
        (currentFrame - 0.1) / video.playbackFrameRate : 0;
        setStatus('rendering');
        lastFrame.current = currentFrame;
        worker.renderFrame(videoData, time).then(data => {
          if (currentFrame === renderingFrame.current) {
            setFrameData(data);
            setStatus('rendered');
          } else {
            setStatus('ready');
          }
        }).catch(error => {
          console.log(error);
          setStatus('error');
          setFrameData(undefined);
        });
      }
    }
  }, [currentFrame, isPlaying, status, video == null ? void 0 : video.playbackFrameRate, worker, videoData]);
  return {
    frame: frameData,
    status
  };
}

const reducer = produce((state, action) => {
  function updateReading(id) {
    const picker = state.pickers[id];
    if (state.frameData) {
      const {
        color,
        reading,
        type
      } = getColorReading({
        frameData: state.frameData,
        x: picker.x,
        y: picker.y,
        r: picker.r,
        sampleMethod: picker.config.sampleMethod,
        palette: picker.config.palette,
        formula: picker.config.formula,
        precision: picker.config.precision,
        precisionType: picker.config.precisionType
      });
      picker.color = color;
      picker.reading = reading;
      picker.type = type;
    }
  }
  switch (action.action) {
    case 'SHOW':
      {
        state.pickers[action.id] = {
          id: action.id,
          x: action.config.x,
          y: action.config.y,
          r: action.config.r,
          isFocused: false,
          config: action.config
        };
        updateReading(action.id);
        break;
      }
    case 'HIDE':
      {
        delete state.pickers[action.id];
        break;
      }
    case 'FOCUS':
      {
        state.pickers[action.id].isFocused = action.isFocused;
        break;
      }
    case 'MOVE':
      {
        const picker = state.pickers[action.id];
        picker.x = action.x;
        picker.y = action.y;
        updateReading(action.id);
        break;
      }
    case 'RESIZE':
      {
        state.pickers[action.id].r = action.r;
        updateReading(action.id);
        break;
      }
    case 'UPDATE':
      {
        state.frameData = freeze(action.frameData);
        if (state.frameData) {
          const avgColor = getAvgColor(state.frameData);
          state.uiColor = avgColor < 128 ? 'white' : 'black';
          for (const picker of Object.values(state.pickers)) {
            updateReading(picker.id);
          }
        }
      }
  }
});
const ColorMeasurementContext = createContext(null);
const ColorMeasurementProvider = ({
  children,
  tools,
  dispatch: toolsDispatch
}) => {
  const [{
    pickers,
    uiColor
  }, dispatch] = useReducer(reducer, {
    pickers: {},
    uiColor: 'black'
  });
  const {
    frame,
    status
  } = useFrameData();
  useEffect(() => {
    dispatch({
      action: 'UPDATE',
      frameData: frame
    });
  }, [frame]);
  useEffect(() => {
    for (const picker of Object.values(pickers)) {
      if (tools.every(tool => tool.id !== picker.id)) {
        dispatch({
          action: 'HIDE',
          id: picker.id
        });
      }
    }
  }, [pickers, tools, toolsDispatch]);
  const value = {
    pickers,
    frameData: frame,
    status,
    uiColor,
    dispatch
  };
  return h(ColorMeasurementContext.Provider, {
    value: value
  }, children);
};
function useColorMeasurements() {
  const context = useContext(ColorMeasurementContext);
  if (!context) {
    throw new Error('useColorMeasurements must be used within ColorMeasurementProvider');
  }
  const {
    dispatch
  } = context;
  return {
    pickers: context.pickers,
    uiColor: context.uiColor,
    frameData: context.frameData,
    status: context.status,
    resize: useCallback((id, r) => dispatch({
      action: 'RESIZE',
      id,
      r
    }), [dispatch]),
    update: useCallback(frameData => dispatch({
      action: 'UPDATE',
      frameData
    }), [dispatch]),
    close: useCallback(id => dispatch({
      action: 'HIDE',
      id
    }), [dispatch])
  };
}
function useColorMeasurement(id, config) {
  const context = useContext(ColorMeasurementContext);
  if (!context) {
    throw new Error('useColorMeasurements must be used within ColorMeasurementProvider');
  }
  const {
    dispatch
  } = context;
  const picker = context.pickers[id];
  if (!picker) {
    dispatch({
      action: 'SHOW',
      id,
      config
    });
  }
  return {
    picker,
    uiColor: context.uiColor,
    frameData: context.frameData,
    move: useCallback((x, y) => dispatch({
      action: 'MOVE',
      x,
      y,
      id
    }), [dispatch, id]),
    focus: useCallback(isFocused => dispatch({
      action: 'FOCUS',
      isFocused,
      id
    }), [dispatch, id]),
    resize: useCallback(r => dispatch({
      action: 'RESIZE',
      id,
      r
    }), [dispatch, id])
  };
}

const ColorPicker = ({
  id,
  config
}) => {
  var _picker$x, _picker$y;
  const {
    picker,
    move,
    focus,
    resize,
    frameData
  } = useColorMeasurement(id, config);
  const {
    draggable,
    resizable,
    bounds,
    label
  } = config;
  const {
    width: overlayWidth
  } = useOverlayDimensions();
  const {
    isFocused,
    focusProps
  } = useFocusIn();
  const {
    dragHandleProps,
    onKeyboardDrag
  } = useDragging({
    transform: {
      x: (_picker$x = picker == null ? void 0 : picker.x) != null ? _picker$x : 0.5,
      y: (_picker$y = picker == null ? void 0 : picker.y) != null ? _picker$y : 0.5,
      w: 0,
      h: 0,
      t: 0,
      p: 0
    },
    onTransformChange: ({
      x,
      y
    }) => move(x, y),
    bounds: bounds ? bounds : {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    }
  });
  useEffect(() => {
    focus(isFocused);
  }, [focus, isFocused]);
  function onKeyDown(e) {
    if (draggable) {
      onKeyboardDrag(e);
    }
    if (resizable) {
      let isHandled = true;
      switch (e.code) {
        case 'KeyR':
          resize(Math.min(100, picker.r + (e.shiftKey ? 10 : 1)));
          break;
        case 'KeyF':
          resize(Math.max(1, picker.r - (e.shiftKey ? 10 : 1)));
          break;
        default:
          isHandled = false;
          break;
      }
      if (isHandled) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }
  if (picker) {
    var _frameData$width;
    const videoWidth = (_frameData$width = frameData == null ? void 0 : frameData.width) != null ? _frameData$width : 1920;
    const cssRadius = Math.round((picker.r + 2) / videoWidth * overlayWidth);
    const cssDiameter = cssRadius * 2;
    let uiColor = 'white';
    if (picker.color) {
      const avg = (picker.color[0] + picker.color[1] + picker.color[2]) / 3;
      uiColor = avg < 128 ? 'white' : 'black';
    }
    return h(PointerTarget, draggable && dragHandleProps, h("div", _extends({
      className: `
          p-absolute
          focus:p-outline-none
          ${draggable ? 'p-cursor-move' : 'p-cursor-pointer'}
        `,
      style: {
        transform: 'translate(-50%, -50%)',
        left: `${(picker.x * 100).toFixed(2)}%`,
        top: `${(picker.y * 100).toFixed(2)}%`,
        zIndex: isFocused ? 40 : 29,
        minWidth: 32,
        minHeight: 32
      },
      tabIndex: 0,
      role: "group",
      "aria-label": "Color Tool"
    }, focusProps, {
      onKeyDown: draggable || resizable ? onKeyDown : undefined
    }), h("div", {
      className: "p-m-auto p-text-black",
      style: {
        width: cssDiameter,
        height: cssDiameter,
        marginTop: `max(0px, (100% - ${cssDiameter}px) / 2)`
      }
    }, isFocused ? h("div", {
      className: `
                  p-absolute p-py-1 p-px-2 
                  p-text-xs p-leading-none p-font-bold p-whitespace-nowrap
                  p-top-0
                  ${uiColor === 'black' ? 'p-text-white p-border-gray-800 p-bg-gray-800' : 'p-text-black p-border-white p-bg-white'}
                  ${picker.x <= 0.75 && picker.y >= 0.35 ? 'p-tri-bl' : ''}
                  ${picker.x <= 0.75 && picker.y < 0.35 ? 'p-tri-tl' : ''}
                  ${picker.x > 0.75 && picker.y < 0.35 ? 'p-tri-tr' : ''}
                  ${picker.x > 0.75 && picker.y >= 0.35 ? 'p-tri-br' : ''}
                `,
      style: {
        [picker.x <= 0.75 ? 'left' : 'right']: Math.max(16, cssRadius) - 4,
        transform: `translate(0, -50%) translate(0, ${Math.max(16, cssRadius) + (picker.y >= 0.35 ? -1 : 1) * (cssRadius + 24)}px)`
      }
    }, label.toUpperCase()) : null, h("svg", {
      className: `
                p-overflow-visible p-stroke-current p-w-full p-h-full
                ${uiColor === 'black' ? 'p-text-black' : 'p-text-white'}
              `,
      width: cssDiameter,
      height: cssDiameter,
      viewBox: `${-cssDiameter / 2} ${-cssDiameter / 2} ${cssDiameter} ${cssDiameter}`,
      "aria-hidden": true
    }, h("g", null, h("circle", {
      cx: "0",
      cy: "0",
      r: "50%",
      fill: "transparent",
      strokeWidth: 1
    }), h("line", {
      x1: -cssRadius - 3,
      y1: "0",
      x2: -cssRadius + 3,
      y2: 0,
      strokeWidth: 1
    }), h("line", {
      x1: cssRadius - 3,
      y1: "0",
      x2: cssRadius + 3,
      y2: 0,
      strokeWidth: 1
    }), h("line", {
      x1: 0,
      y1: cssRadius - 3,
      x2: 0,
      y2: cssRadius + 3,
      strokeWidth: 1
    }), h("line", {
      x1: 0,
      y1: -cssRadius - 3,
      x2: 0,
      y2: -cssRadius + 3,
      strokeWidth: 1
    }))))));
  } else {
    return null;
  }
};

function lerp(min, max, x) {
  x = Math.min(1, Math.max(0, x));
  return Math.round(min + x * (max - min));
}
const Slider = ({
  className: _className = '',
  min,
  max,
  value,
  onChange,
  label,
  dark: _dark = false
}) => {
  function onKeyDown(e) {
    let isHandled = true;
    switch (e.code) {
      case 'ArrowRight':
        onChange(Math.min(max, value + (e.shiftKey ? 10 : 1)));
        break;
      case 'ArrowLeft':
        onChange(Math.max(min, value - (e.shiftKey ? 10 : 1)));
        break;
      default:
        isHandled = false;
        break;
    }
    if (isHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }
  return h("div", {
    className: `
				p-relative p-rounded-full
				${_dark ? 'p-bg-gray-600' : 'p-bg-gray-400'}
				focus-within:p-shadow-focus
				${_className}
			`,
    style: {
      height: 6
    },
    onKeyDown: onKeyDown
  }, h(PointerBounds, {
    onClick: e => onChange(lerp(min, max, e.x))
  }, h("div", {
    className: "p-absolute p-mx-1 p-h-full p-inset-x-0"
  }, h(PointerTarget, {
    onDrag: e => onChange(lerp(min, max, e.x))
  }, h("div", {
    className: `
								p-absolute p-h-12 p-w-12 p--ml-6 p-flex p-items-center p-justify-center
								focus:p-outline-none
							`,
    style: {
      left: `${(value - min) / (max - min) * 100}%`,
      marginTop: -21
    },
    role: "slider",
    tabIndex: 0,
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    "aria-label": label,
    "aria-orientation": "horizontal"
  }, h("div", {
    className: `
									p-rounded-full p-h-3 p-w-3
									${_dark ? 'p-bg-white' : 'p-bg-black'}
								`
  }))))));
};

const ColorReadout = ({
  config: {
    x,
    y,
    draggable,
    bounds
  },
  dispatch
}) => {
  const {
    isPlaying
  } = usePlayback();
  const {
    pickers,
    uiColor,
    status,
    resize
  } = useColorMeasurements();
  const [transform, setTransform] = useTransform({
    x,
    y
  });
  const {
    isFocused,
    focusProps
  } = useFocusIn();
  const {
    dragHandleProps,
    onKeyboardDrag
  } = useDragging({
    transform,
    onTransformChange: setTransform,
    bounds: bounds ? bounds : {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    }
  });
  const [hiddenDisplays, setHiddenDisplays] = useState([]);
  function toggle(id) {
    if (hiddenDisplays.includes(id)) {
      setHiddenDisplays(hiddenDisplays.filter(_id => _id !== id));
    } else {
      setHiddenDisplays([...hiddenDisplays, id]);
    }
  }
  function close(id) {
    dispatch({
      action: 'REMOVE',
      id
    });
  }
  // Reset the readout position when all color pickers are closed.
  useEffect(() => {
    if (Object.keys(pickers).length === 0) {
      setTransform(transform => Object.assign({}, transform, {
        x,
        y
      }));
    }
  }, [pickers, setTransform, x, y]);
  if (Object.values(pickers).length > 0) {
    return h(PointerTarget, draggable && dragHandleProps, h("div", _extends({
      className: `
          p-absolute
          focus:p-outline-none focus:p-shadow-focus
          ${draggable ? 'p-cursor-move' : 'p-cursor-pointer'}
        `,
      style: {
        transform: 'translate(-50%, -50%)',
        left: `${(transform.x * 100).toFixed(2)}%`,
        top: `${(transform.y * 100).toFixed(2)}%`,
        zIndex: isFocused ? 40 : 29
      },
      tabIndex: 0,
      role: "group",
      "aria-label": "Color Tool"
    }, focusProps, {
      onKeyDown: draggable ? onKeyboardDrag : undefined
    }), Object.values(pickers).map(picker => {
      var _picker$config$units;
      return h("div", {
        className: `
                p-mb-px p-whitespace-nowrap
                ${uiColor === 'black' ? 'p-bg-black p-text-white' : 'p-bg-white p-text-black'}
              `
      }, h("div", {
        className: `
                p-border-b p-flex p-leading-none
                ${uiColor === 'black' ? 'p-border-gray-700' : 'p-border-gray-400'}
              `
      }, h("button", {
        className: "p-p-2 focus:p-outline-none focus:p-bg-gray-400 hover:p-bg-gray-400",
        onClick: () => toggle(picker.id),
        type: "button"
      }, h(Icon, {
        className: "p-fill-current",
        icon: hiddenDisplays.includes(picker.id) ? 'caret-down' : 'caret-up'
      })), h("h4", {
        className: "p-py-2 p-leading-none p-font-bold p-text-xs p-flex-grow"
      }, picker.config.label.toUpperCase()), h("button", {
        className: "p-p-2 focus:p-outline-none focus:p-bg-gray-400 hover:p-bg-gray-400",
        onClick: () => close(picker.id),
        type: "button"
      }, h(Icon, {
        className: "p-fill-current",
        icon: "close-2"
      }))), hiddenDisplays.includes(picker.id) ? null : h("div", {
        className: "p-flex p-p-2"
      }, status !== 'rendered' ? status !== 'error' ? h("div", {
        className: "p-flex "
      }, h(Loading, {
        size: "sm"
      }), h("span", {
        className: "p-ml-2 p-text-sm"
      }, isPlaying ? 'Pause video to make a measurement' : status === 'downloading' ? 'Downloading video' : 'Processing frame')) : 'Change frame to make a measurement' : h(Fragment, null, h("div", {
        className: `
                          p-w-8 p-h-8 p-mr-2 p-border
                          ${uiColor === 'black' ? 'p-border-gray-700' : 'p-border-gray-400'}
                        `,
        style: {
          backgroundColor: picker.color ? `rgb(${picker.color[0]},${picker.color[1]},${picker.color[2]})` : 'white'
        }
      }), h("div", {
        className: "p-text-xs p-font-bold p-leading-none p-mt-px"
      }, h("div", {
        className: "p-font-mono "
      }, picker.type === 'min' ? 'less than ' : picker.type === 'max' ? 'greater than ' : '', picker.reading, " ", (_picker$config$units = picker.config.units) != null ? _picker$config$units : ''), picker.config.resizable ? h("div", {
        className: "p-mt-2 p-inline-block",
        onTouchStart: e => e.stopPropagation(),
        onMouseDown: e => e.stopPropagation()
      }, h("span", null, "Radius:"), h(Slider, {
        className: "p-ml-2 p-inline-block p-w-24",
        min: 1,
        max: 100,
        value: picker.r,
        onChange: r => resize(picker.id, r),
        label: `${picker.config.label} radius`,
        dark: uiColor === 'black'
      })) : null))));
    })));
  } else {
    return null;
  }
};

const Tools = ({
  colorReadout
}) => {
  const {
    tools,
    dispatch
  } = useTools();
  const toolElements = tools.map(tool => {
    const props = {
      key: tool.id,
      id: tool.id,
      config: tool.config,
      dispatch
    };
    switch (tool.config.type) {
      case ToolType.Stopwatch:
        return h(Stopwatch, props);
      case ToolType.Ruler:
        return h(Ruler, props);
      case ToolType.Grid:
        return h(Grid, props);
      case ToolType.Image:
        return h(ImageTool, props);
      case ToolType.Protractor:
        return h(Protractor, props);
      case ToolType.Calibration:
        return h(Calibration, null);
      case ToolType.Color:
        return h(ColorPicker, props);
    }
  });
  return colorReadout ? h(ColorMeasurementProvider, {
    tools: tools,
    dispatch: dispatch
  }, toolElements, h(ColorReadout, {
    config: colorReadout,
    dispatch: dispatch
  })) : h(Fragment, null, toolElements);
};

const ColorOption = forwardRef(({
  selected,
  value,
  label,
  first: _first = false,
  last: _last = false,
  onChange
}, ref) => {
  const id = useComponentId();
  const isSelected = selected == value;
  return h("div", null, h("input", {
    ref: ref,
    id: `color-${id}`,
    type: "radio",
    name: "color",
    value: value,
    checked: isSelected,
    className: "p-opacity-0 p-absolute",
    onChange: () => onChange && onChange(value)
  }), h("label", {
    for: `color-${id}`,
    className: "p-block p-h-12 p-pt-6 p--mt-6"
  }, h("div", {
    className: `
              p-px-4
              ${_first ? 'p-rounded-l' : ''}
              ${_last ? 'p-rounded-r' : 'p-border-r'}
              ${isSelected ? 'p-bg-black p-text-white' : 'p-bg-white p-text-black'}
            `
  }, label)));
});
ColorOption.displayName = 'Color Option';
const CalibrationToolbar = () => {
  const id = useComponentId();
  const {
    text,
    setDistance,
    color,
    setColor,
    inputError,
    reset
  } = useCalibration();
  const distanceId = `distance-${id}`;
  return h("div", {
    role: "group",
    "aria-label": "Calibration Toolbar",
    className: "p-flex p-px-4 p-py-1"
  }, h("div", null, h("label", {
    for: distanceId,
    className: "p-font-bold p-text-xs p-leading-none p-mb-1 p-pt-2"
  }, "Calibration Distance"), h("input", {
    id: distanceId,
    className: `
            p-block p-rounded p-px-2 p-h-6 focus:p-outline-none focus:p-shadow-focus
            ${inputError ? 'p-text-red-700' : ''}
          `,
    value: text,
    placeholder: "Example: 100 cm",
    onChange: e => setDistance(e.currentTarget.value),
    onKeyDown: e => e.stopPropagation()
  })), h("div", {
    className: "p-pl-4"
  }, h("label", {
    className: "p-font-bold p-text-xs p-leading-none p-mb-1 p-pt-2",
    id: `color-label-${id}`
  }, "Tool Color"), h("div", {
    role: "radiogroup",
    className: "p-flex p-h-6 p-rounded focus-within:p-shadow-focus",
    "aria-labelledby": `color-label-${id}`,
    onKeyDown: e => {
      if (['ArrowUp', 'ArrowLeft', 'ArrowRight', 'ArrowDown'].includes(e.code)) {
        e.stopPropagation();
      }
    }
  }, h(ColorOption, {
    selected: color,
    first: true,
    value: "white",
    label: "White",
    onChange: setColor
  }), h(ColorOption, {
    selected: color,
    value: "black",
    label: "Black",
    onChange: setColor
  }), h(ColorOption, {
    selected: color,
    value: "green",
    label: "Green",
    onChange: setColor
  }), h(ColorOption, {
    selected: color,
    last: true,
    value: "red",
    label: "Red",
    onChange: setColor
  }))), h("div", {
    className: "p-pl-4"
  }, h("button", {
    type: "button",
    className: "p-group p-block p-h-12 p-pt-6 focus:p-outline-none",
    onClick: reset
  }, h("div", {
    className: "p-rounded p-bg-black p-text-white p-px-4 group-focus:p-shadow-focus"
  }, "Reset"))));
};

const _excluded$2 = ["children", "secondary", "onClick", "type", "disabled", "className"];
const Button = forwardRef((_ref, ref) => {
  let {
      children,
      secondary,
      onClick,
      type = 'button',
      disabled,
      className = ''
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  return h("button", _extends({
    ref: ref
  }, props, {
    type: type,
    className: `${className} p-h-12 p-py-2 focus:p-outline-none p-group p-overflow-visible`,
    disabled: disabled,
    onClick: e => {
      if (onClick) {
        e.stopPropagation();
        // We have to cast this to never because of this bug in preact:
        // https://github.com/preactjs/preact/issues/3137
        onClick.call(e.currentTarget, e);
      }
    }
  }), h("div", {
    className: `
          p-px-4 p-rounded p-h-full p-flex p-items-center p-justify-center
          group-focus:p-shadow-focus
          ${secondary ? 'p-bg-white p-text-black' : 'p-bg-black p-text-white'}
          ${disabled ? 'p-opacity-50' : ''}
        `
  }, children));
});
Button.displayName = 'Button';

const ParameterDisplay = ({
  expanded,
  title,
  value
}) => {
  const id = useComponentId();
  const labelId = `param-${id}-label`;
  if (expanded) {
    return h("div", {
      className: "p-p-2 p-flex-shrink-0",
      role: "group",
      "aria-labeledby": labelId
    }, h("label", {
      id: labelId,
      className: "p-font-bold p-text-xs p-leading-none",
      "aria-hidden": true
    }, title.toUpperCase(), h("span", {
      className: "p-sr-only"
    }, "Parameter")), h("p", {
      className: "p-pt-2 p-text-base p-leading-none",
      "aria-hidden": true
    }, value), h("p", {
      className: "p-sr-only"
    }, title, " parameter: ", value));
  } else {
    return h("div", {
      className: "p-mr-6 p-h-12 p-py-2"
    }, h("p", {
      className: "p-font-bold p-text-xs p-leading-none p-mb-1",
      "aria-hidden": true
    }, title.toUpperCase()), h("p", {
      className: "p-text-base p-leading-none",
      "aria-hidden": true
    }, value), h("p", {
      className: "p-sr-only"
    }, title, " parameter: ", value));
  }
};

const ParameterSelector = forwardRef(({
  expanded,
  name,
  title,
  value,
  options,
  validOptions,
  independent,
  enableIndependence: _enableIndependence = false,
  onChange,
  onIndependentChange
}, ref) => {
  const id = useComponentId();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [focused, setFocus] = useState(false);
  const labelId = `param-${id}-label`;
  const groupTypeId = `param-${id}-type`;
  const parameterListbox = useRef(null);
  // When the parameter value changes, update the selected index.
  useEffect(() => {
    const i = options.findIndex(option => option === value);
    setSelectedIndex(oldIndex => i >= 0 ? i : oldIndex);
  }, [options, value]);
  // When selecting an item from the list deselect the parameter if it is disabled.
  function select(i, isClick = false) {
    const option = options[i];
    if (onChange) {
      if (!validOptions.includes(option) || isClick && value === option) {
        onChange({
          name
        });
      } else {
        onChange({
          name,
          value: option
        });
      }
    }
    setSelectedIndex(i);
  }
  useImperativeHandle(ref, () => {
    return {
      focus() {
        var _parameterListbox$cur;
        (_parameterListbox$cur = parameterListbox.current) == null || _parameterListbox$cur.focus();
      }
    };
  });
  if (expanded) {
    return h("div", {
      className: "p-p-2 p-flex-shrink-0",
      role: "group",
      "aria-labeledby": labelId,
      "aria-describedby": groupTypeId
    }, h("span", {
      hidden: true,
      id: groupTypeId
    }, independent ? 'Independent Parameter' : 'Constant Parameter'), h("label", {
      id: labelId,
      className: "p-font-bold p-text-xs p-leading-none",
      "aria-hidden": true
    }, title.toUpperCase(), h("span", {
      className: "p-sr-only"
    }, "Parameter")), _enableIndependence && h("label", {
      className: "p-flex p-items-center p-pt-1 p-pb-3 p-pr-1 p-cursor-pointer"
    }, h("div", {
      className: "p-rounded p-bg-white p-w-5 p-h-5 p-p-1 p-mr-2 focus-within:p-shadow-focus"
    }, h("input", {
      type: "checkbox",
      className: "p-opacity-0 p-absolute p-cursor-pointer",
      checked: independent,
      onClick: () => onIndependentChange && onIndependentChange(independent ? undefined : name),
      onKeyDown: e => {
        if (e.code === 'Space') {
          e.stopPropagation();
        }
      }
    }), independent && h(Icon, {
      icon: "check",
      className: "p-w-full p-h-full"
    })), h("span", {
      "aria-hidden": true
    }, "Independent"), h("span", {
      className: "p-sr-only"
    }, title, " Independent")), h("div", {
      ref: parameterListbox,
      className: "p-group p-bg-white p-rounded p-min-w-24 p-h-44 p-overflow-x-auto focus:p-shadow-focus focus:p-outline-none",
      role: "listbox",
      tabIndex: 0,
      "aria-labelledby": labelId,
      "aria-activedescendant": `param-${id}-option-${selectedIndex}`,
      onFocus: () => setFocus(true),
      onBlur: () => setFocus(false),
      onKeyDown: e => {
        switch (e.code) {
          case 'ArrowUp':
            {
              e.preventDefault();
              e.stopPropagation();
              const newIndex = selectedIndex - 1;
              select(newIndex >= 0 ? newIndex : options.length - 1);
              break;
            }
          case 'ArrowDown':
            {
              e.preventDefault();
              e.stopPropagation();
              const newIndex = selectedIndex + 1;
              select(newIndex < options.length ? newIndex : 0);
              break;
            }
          case 'Enter':
            {
              e.preventDefault();
              e.stopPropagation();
              select(selectedIndex, true);
            }
        }
      }
    }, options.map((option, i) => {
      const disabled = !validOptions.includes(option);
      const selected = value === option;
      const highlighted = focused && selectedIndex === i || selected;
      return h("div", {
        key: option,
        id: `param-${id}-option-${i}`,
        className: `
                    p-h-8 p-pl-3 p-flex p-items-center p-cursor-pointer
                    ${highlighted ? 'p-bg-blue-300' : disabled ? 'p-bg-gray-400' : ''}
                    ${disabled ? 'p-opacity-75' : ''}
                    ${selected ? '' : 'p-pr-8'}
                  `,
        role: "option",
        "aria-selected": highlighted,
        "aria-disabled": disabled,
        onClick: () => select(i, true)
      }, h("span", {
        "aria-hidden": true,
        className: "p-flex p-items-center p-w-full"
      }, h("span", {
        className: "p-flex-grow"
      }, option), selected && h(Icon, {
        icon: "check",
        className: "p-w-8 p-h-8 p-px-2"
      })), h("span", {
        class: "p-sr-only"
      }, title, " ", option));
    })));
  } else {
    return h("div", {
      className: "p-mr-6 p-h-12 p-py-2"
    }, h("p", {
      className: "p-font-bold p-text-xs p-leading-none p-mb-1",
      "aria-hidden": true
    }, title.toUpperCase()), h("p", {
      className: "p-text-base p-leading-none",
      "aria-hidden": true
    }, value), h("p", {
      className: "p-sr-only"
    }, title, " parameter: ", value));
  }
});

const VideoSelection = () => {
  const {
    isExpanded,
    canExpand,
    parameters,
    independent,
    enableIndependence,
    dispatch
  } = useSelection();
  const toggleElement = useRef(null);
  const invalid = parameters.some(param => !param.value);
  const firstSelector = useRef(null);
  useEffect(() => {
    if (isExpanded) {
      var _firstSelector$curren;
      (_firstSelector$curren = firstSelector.current) == null || _firstSelector$curren.focus();
    }
  }, [isExpanded]);
  return h("form", {
    id: "selection-panel",
    "data-testid": "selection-root",
    className: `p-bg-gray-400 p-flex p-items-bottom p-py-2 ${isExpanded ? 'p-absolute p-bottom-0 p-w-full' : canExpand ? 'p-cursor-pointer' : ''}`,
    onSubmit: e => {
      e.stopPropagation();
      e.preventDefault();
      dispatch({
        type: 'LOAD'
      });
    },
    onClick: isExpanded ? undefined : () => dispatch({
      type: 'OPEN'
    }),
    onKeyDown: isExpanded ? e => {
      if (e.code === 'Escape') {
        dispatch({
          type: 'CANCEL'
        });
      }
    } : undefined,
    role: "group",
    "aria-label": "Selection Panel"
  }, h("div", {
    className: `
          p-flex p-flex-grow p-flex-shrink p-max-w-full
          ${isExpanded ? 'p-pl-2' : 'p-pl-4'}
        `
  }, h("div", {
    className: "p-flex p-flex-shrink p-overflow-x-auto"
  }, parameters.filter(param => !param.selectable && param.visible).map(param => h(ParameterDisplay, _extends({}, param, {
    key: param.name,
    expanded: isExpanded
  }))), parameters.filter(param => param.selectable).map((param, i) => h(ParameterSelector, _extends({}, param, {
    ref: i === 0 ? firstSelector : undefined,
    key: param.name,
    expanded: isExpanded,
    independent: param.name === independent,
    enableIndependence: enableIndependence,
    onChange: e => dispatch({
      type: 'PARAMETER',
      name: e.name,
      value: e.value
    }),
    onIndependentChange: name => dispatch({
      type: 'INDEPENDENT',
      name
    })
  })))), h("div", {
    className: `
              p-pr-4 p-pl-2
            `
  }, isExpanded ? h("div", {
    className: "p-flex p-flex-col p-items-stretch p-mt-6"
  }, h(Button, {
    ref: toggleElement,
    type: "submit",
    disabled: invalid,
    "aria-controls": "selection-panel",
    "aria-expanded": isExpanded
  }, h("span", {
    "aria-hidden": true
  }, "Load"), h("span", {
    className: "p-sr-only"
  }, "Load Selection")), h(Button, {
    secondary: true,
    onClick: () => dispatch({
      type: 'CANCEL'
    }),
    "aria-controls": "selection-panel",
    "aria-expanded": isExpanded
  }, h("span", {
    "aria-hidden": true
  }, "Cancel"), h("span", {
    className: "p-sr-only"
  }, "Cancel Selection")), h(Button, {
    secondary: true,
    onClick: () => dispatch({
      type: 'CLEAR'
    })
  }, h("span", {
    "aria-hidden": true
  }, "Clear"), h("span", {
    className: "p-sr-only"
  }, "Clear Selection"))) : canExpand ? h(Button, {
    className: "p-mr-4",
    ref: toggleElement,
    onClick: () => dispatch({
      type: 'OPEN'
    }),
    "aria-controls": "selection-panel",
    "aria-expanded": isExpanded
  }, h("span", {
    "aria-hidden": true
  }, "Change"), h("span", {
    className: "p-sr-only"
  }, "Change Selection")) : null)));
};

const SecondaryToolbar = () => {
  const {
    isSmallScreen
  } = useFullScreen();
  const {
    tools
  } = useTools();
  const {
    enabled
  } = useSelection();
  const toolbars = [];
  if (tools.some(tool => tool.config.type === ToolType.Calibration)) {
    toolbars.push(h(CalibrationToolbar, null));
  }
  if (enabled) {
    toolbars.push(h(VideoSelection, null));
  }
  return toolbars.length > 0 && !isSmallScreen ? h("div", {
    id: "secondary-toolbar",
    className: "p-relative p-h-16 p-bg-gray-400 p-z-30"
  }, toolbars[0]) : null;
};

const SmallScreenOverlay = () => {
  const {
    fullScreenEnabled,
    isSmallScreen,
    toggleFullScreen
  } = useFullScreen();
  return fullScreenEnabled && isSmallScreen ? h("div", {
    className: "p-absolute p-inset-0 p-w-full p-h-full p-z-60 p-bg-shadow p-flex p-flex-col p-justify-center p-items-center"
  }, h(Button, {
    secondary: true,
    onClick: toggleFullScreen
  }, "Launch Player")) : null;
};

const _excluded$1 = ["disableFullscreen", "preview", "onCalibrationChange", "onError"];
const Instance = _ref => {
  let {
      disableFullscreen = false,
      preview = false,
      onCalibrationChange,
      onError
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  const settings = useSettings({
    settings: props.settings
  });
  return h("div", {
    className: "pivotplayer"
  }, h(PlaybackProvider, null, h(SelectionProvider, {
    config: settings.selection,
    videos: settings.videos
  }, h(ToolProvider, {
    tools: settings.tools
  }, h(CalibrationProvider, {
    config: settings.calibration,
    onCalibrationChange: onCalibrationChange
  }, h(FullScreenProvider, {
    enabled: !disableFullscreen
  }, h(SmallScreenOverlay, null), h(Player, {
    preview: preview
  }, h(Video, {
    preview: preview,
    onError: onError
  }, h(VideoOverlay, null, h(Tools, {
    colorReadout: settings.colorReadout
  })), h(Toolbar, null)), h(SecondaryToolbar, null), h(VideoControls, null))))))));
};

const _excluded = ["el", "disableFullscreen", "preview", "onCalibrationChange", "onError"];
class PivotPlayer {
  constructor(_ref) {
    let {
        el,
        disableFullscreen,
        preview,
        onCalibrationChange,
        onError
      } = _ref,
      settings = _objectWithoutPropertiesLoose(_ref, _excluded);
    render(h(Instance, {
      disableFullscreen,
      preview,
      settings,
      onCalibrationChange,
      onError
    }), el, el.firstElementChild);
    this._el = el;
  }
  destroy() {
    render(null, this._el);
  }
}

export { PivotPlayer as default };
